import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import LanguageSwitcher from './LanguageSwitcher';
import TraderPage from './Trader';
import {
  Package, Plus, Copy, Trash2, Settings as SettingsIcon, LogOut, Search, X, Check,
  AlertCircle, ExternalLink, Edit2, Share2, Globe, Lock, Bell,
  Building2, Users, BarChart3, ChevronRight, Menu, Home, RefreshCw,
  MapPin, Send, FileText
} from 'lucide-react';

const API_URL = '';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

// --- AuthProvider и другие системные компоненты ---
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      // 1. Проверяем, пришел ли токен в URL после логина
      const params = new URLSearchParams(window.location.search);
      const tokenFromUrl = params.get('token');

      if (tokenFromUrl) {
        // Сохраняем токен в память браузера
        localStorage.setItem('session_token', tokenFromUrl);
        // Убираем токен из адресной строки, чтобы она была чистой
        window.history.replaceState({}, document.title, location.pathname);
      }

      const token = localStorage.getItem('session_token');
      
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        // Проверяем токен на сервере
        const res = await api.get('/api/auth/me');
        setUser(res.data);
        // Синхронизируем язык с бэкендом при логине
        const savedLang = localStorage.getItem('i18nextLng') || 'en';
        if (savedLang !== 'en' || res.data?.language !== savedLang) {
          await api.post('/api/settings/filters', { language: savedLang });
        }
      } catch (err) {
        console.error("Auth error:", err);
        localStorage.removeItem('session_token');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [location.pathname]); // Срабатывает при смене пути или возврате с логина

  const logout = () => {
    localStorage.removeItem('session_token');
    setUser(null);
    navigate('/');
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function LoadingScreen() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="font-mono text-primary text-xl animate-pulse uppercase">
          {t('system.initializing')}
        </div>
      </div>
    </div>
  );
}

// --- Компонент настроек (ДОБАВЛЕНО) ---
function SettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [settingsTab, setSettingsTab] = useState(() => localStorage.getItem('settings_tab') || 'lists');
  const [filters, setFilters] = useState('');
  const [showJitaPrices, setShowJitaPrices] = useState(true);
  const [notificationTz, setNotificationTz] = useState('UTC');
  const [jitaPriceType, setJitaPriceType] = useState('sell');
  const [salesTax, setSalesTax] = useState(7.5);
  const [accountingLevel, setAccountingLevel] = useState(0);
  const [kbtTax, setKbtTax] = useState(0.5);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const traderStationsSaveRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'trader' || tab === 'orders') {
      setSettingsTab(tab);
      localStorage.setItem('settings_tab', tab);
    }
  }, [searchParams]);

  useEffect(() => {
    localStorage.setItem('settings_tab', settingsTab);
  }, [settingsTab]);

  const timezones = [
    'UTC-12', 'UTC-11', 'UTC-10', 'UTC-9', 'UTC-8', 'UTC-7', 'UTC-6', 'UTC-5',
    'UTC-4', 'UTC-3', 'UTC-2', 'UTC-1', 'UTC', 'UTC+1', 'UTC+2', 'UTC+3',
    'UTC+4', 'UTC+5', 'UTC+6', 'UTC+7', 'UTC+8', 'UTC+9', 'UTC+10', 'UTC+11', 'UTC+12',
  ];

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/api/settings/filters');
        console.log("Settings response:", response.data);
        setFilters(response.data.filters || '');
        setShowJitaPrices(response.data.show_jita_prices === true);
        setNotificationTz(response.data.notification_tz || 'UTC');
        setJitaPriceType(response.data.jita_price_type || 'sell');
        setSalesTax(response.data.sales_tax != null ? response.data.sales_tax : 7.5);
        setAccountingLevel(response.data.accounting_level || 0);
        setKbtTax(response.data.kbt_tax != null ? response.data.kbt_tax : 0.5);
      } catch (err) {
        console.error("Error loading filters:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (settingsTab === 'trader' && traderStationsSaveRef.current) {
        await traderStationsSaveRef.current();
      }
      await api.post('/api/settings/filters', { 
        filters,
        show_jita_prices: showJitaPrices,
        notification_tz: notificationTz,
        jita_price_type: jitaPriceType,
        sales_tax: salesTax,
        kbt_tax: kbtTax,
      });
      alert(t('settings.settingsSaved'));
    } catch (err) {
      console.error("Error saving filters:", err);
      alert(t('settings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-2xl mx-auto">
        {/* Tab buttons */}
        <div className="flex mb-6 border-b border-zinc-800">
          <button onClick={() => setSettingsTab('lists')}
            className={`px-5 py-3 text-sm font-mono uppercase tracking-wider transition-colors ${settingsTab === 'lists' ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-zinc-500 hover:text-zinc-300'}`}>
            {t('settings.listSettings')}
          </button>
          <button onClick={() => setSettingsTab('orders')}
            className={`px-5 py-3 text-sm font-mono uppercase tracking-wider transition-colors ${settingsTab === 'orders' ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-zinc-500 hover:text-zinc-300'}`}>
            {t('settings.orderSettings')}
          </button>
          <button onClick={() => setSettingsTab('trader')}
            className={`px-5 py-3 text-sm font-mono uppercase tracking-wider transition-colors ${settingsTab === 'trader' ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-zinc-500 hover:text-zinc-300'}`}>
            {t('trader.trader')}
          </button>
        </div>

        <div className="bg-surface border border-border p-8 rounded-lg shadow-2xl animate-fadeIn">
          <div className="flex items-center gap-3 mb-8">
            <SettingsIcon className="text-primary w-6 h-6" />
            <h1 className="text-2xl font-light tracking-tight text-white uppercase">
              {settingsTab === 'lists' ? t('settings.listSettings') : settingsTab === 'orders' ? t('settings.orderSettings') : t('trader.traderSettings')}
            </h1>
          </div>

          {settingsTab === 'lists' ? (
          <div className="space-y-6">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-text-muted mb-3">
                {t('settings.searchExclusions')}
              </label>
              <textarea
                className="w-full bg-zinc-950 border border-border p-4 text-white font-mono text-sm focus:border-primary outline-none transition-colors"
                rows="4"
                value={filters}
                onChange={(e) => setFilters(e.target.value)}
                placeholder="SKIN, Blueprint, Edition, Package"
              />
              <p className="mt-3 text-xs text-text-muted italic leading-relaxed">
                {t('settings.exclusionsHelp')}
              </p>
            </div>

            <div className="flex items-center justify-between bg-zinc-900/50 border border-border p-4 rounded-lg">
              <div>
                <div className="text-sm font-medium text-white">{t('settings.showJitaPrices')}</div>
                <div className="text-xs text-text-muted mt-1">{t('settings.showJitaPricesHelp')}</div>
              </div>
              <button
                type="button"
                onClick={() => setShowJitaPrices(!showJitaPrices)}
                className={`px-4 py-2 rounded font-medium ${showJitaPrices ? 'bg-green-600' : 'bg-zinc-600'}`}
              >
                {showJitaPrices ? t('settings.on') : t('settings.off')}
              </button>
            </div>

            <div className="bg-zinc-900/50 border border-border p-4 rounded-lg">
              <label className="block text-xs font-mono uppercase tracking-widest text-text-muted mb-2">
                {t('settings.notificationTimezone')}
              </label>
              <select value={notificationTz} onChange={e => setNotificationTz(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-white font-mono outline-none focus:border-cyan-700 rounded-sm">
                {timezones.map(tz => (
                  <option key={tz} value={tz} className="bg-zinc-900">{tz}</option>
                ))}
              </select>
              <p className="mt-2 text-xs text-zinc-500 font-mono">{t('settings.notificationTzHelp')}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-primary text-black font-medium py-3 hover:bg-primary-hover transition-all disabled:opacity-50 uppercase text-sm tracking-tighter"
              >
                {saving ? t('dashboard.saving') : t('settings.applyChanges')}
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="flex-1 border border-border text-white py-3 hover:bg-zinc-900 transition-all uppercase text-sm tracking-tighter"
              >
                {t('settings.backToDashboard')}
              </button>
            </div>
          </div>
          ) : settingsTab === 'orders' ? (
          <div className="space-y-6">
            {/* Characters */}
            <CharacterManager t={t} api={api} user={user} />

            {/* Order notification settings */}
            <OrderNotifySettings t={t} api={api} user={user} />

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex-1 border border-border text-white py-3 hover:bg-zinc-900 transition-all uppercase text-sm tracking-tighter"
              >
                {t('settings.backToDashboard')}
              </button>
            </div>
          </div>
          ) : (
          <div className="space-y-6">
            <TraderStationManager t={t} api={api} user={user} saveRef={traderStationsSaveRef} />

            {/* Marginality Settings */}
            <div className="bg-zinc-900/50 border border-border p-4 rounded-lg space-y-4">
              <label className="text-xs font-mono uppercase tracking-widest text-text-muted">Маржинальность</label>

              {/* KBT tax (global) */}
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Налог КБТ (%)</label>
                <input type="text" inputMode="decimal"
                  value={kbtTax}
                  onChange={e => {
                    const raw = e.target.value;
                    if (raw === '' || /^-?\d*[.,]?\d*$/.test(raw)) {
                      const val = parseFloat(raw.replace(',', '.'));
                      setKbtTax(isNaN(val) ? 0 : Math.round(val * 10) / 10);
                    }
                  }}
                  className="w-24 bg-zinc-950 border border-zinc-700 px-2.5 py-1.5 text-sm text-white font-mono outline-none focus:border-cyan-700 rounded" />
              </div>

              {/* Sales Tax from Accounting skill */}
              <MarginalitySalesTax api={api} user={user} salesTax={salesTax} setSalesTax={setSalesTax}
                accountingLevel={accountingLevel} setAccountingLevel={setAccountingLevel} />
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-primary text-black font-medium py-3 hover:bg-primary-hover transition-all disabled:opacity-50 uppercase text-sm tracking-tighter"
              >
                {saving ? t('dashboard.saving') : t('settings.applyChanges')}
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="flex-1 border border-border text-white py-3 hover:bg-zinc-900 transition-all uppercase text-sm tracking-tighter"
              >
                {t('settings.backToDashboard')}
              </button>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== Character Manager (Settings) =====
function CharacterManager({ t, api, user }) {
  const [chars, setChars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkingUrl, setLinkingUrl] = useState(null);

  const loadChars = async () => {
    try {
      const res = await api.get('/api/characters');
      setChars(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadChars(); }, []);

  // Check if a new character was just linked via URL param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('linked')) {
      loadChars();
      window.history.replaceState({}, '', '/settings');
    }
  }, []);

  const handleAdd = async () => {
    try {
      const res = await api.post('/api/characters');
      if (res.data?.url) window.location.href = res.data.url;
    } catch (e) { console.error(e); }
  };

  const handleSetMain = async (charId) => {
    try {
      await api.post(`/api/characters/${charId}/main`);
      loadChars();
    } catch (e) { console.error(e); }
  };

  const handleRemove = async (charId) => {
    try {
      await api.delete(`/api/characters/${charId}`);
      loadChars();
    } catch (e) { console.error(e); }
  };

  if (loading) return null;

  return (
    <div className="bg-zinc-900/50 border border-border p-4 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <label className="text-xs font-mono uppercase tracking-widest text-text-muted">{t('settings.characters')}</label>
        <button onClick={handleAdd} className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-2 py-1 rounded font-mono transition-colors">
          + {t('settings.addCharacter')}
        </button>
      </div>
      {chars.length === 0 ? (
        <p className="text-xs text-zinc-600 italic">{t('settings.noCharacters')}</p>
      ) : (
        <div className="space-y-2">
          {chars.map(ch => (
            <div key={ch.char_id} className="flex items-center justify-between bg-black border border-zinc-800 px-3 py-2 rounded-sm">
              <div className="flex items-center gap-2">
                <span className="text-sm text-white font-mono">{ch.char_name}</span>
                {ch.is_main && <span className="text-[10px] text-cyan-400 font-mono">({t('settings.main')})</span>}
              </div>
              <div className="flex items-center gap-2">
                {!ch.is_main && (
                  <>
                    <button onClick={() => handleSetMain(ch.char_id)} className="text-[10px] text-zinc-500 hover:text-cyan-400 font-mono transition-colors">{t('settings.makeMain')}</button>
                    <button onClick={() => handleRemove(ch.char_id)} className="text-[10px] text-zinc-500 hover:text-red-400 font-mono transition-colors">{t('common.delete')}</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== Trader Station Manager =====
const stationIdKey = (id) => String(id);

const isTruthyTracked = (v) => v === true || v === 1;

const isPlaceholderStationName = (s) => !s?.name || String(s.name) === String(s.structure_id);

function mergeTraderStations(trackedList, allStationsList) {
  const byId = new Map();
  for (const s of [...(trackedList || []), ...(allStationsList || [])]) {
    const id = stationIdKey(s.structure_id);
    const entry = { ...s, structure_id: id };
    const prev = byId.get(id);
    if (!prev) {
      byId.set(id, entry);
      continue;
    }
    const name = !isPlaceholderStationName(entry) ? entry.name : prev.name;
    byId.set(id, {
      ...prev,
      ...entry,
      structure_id: id,
      name,
      tracked: isTruthyTracked(prev.tracked) || isTruthyTracked(entry.tracked),
      margin_enabled: !!(prev.margin_enabled || entry.margin_enabled),
      broker_fee: entry.broker_fee ?? prev.broker_fee,
      kbt_tax: entry.kbt_tax ?? prev.kbt_tax,
    });
  }
  return [...byId.values()].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
}

function TraderStationManager({ t, api, user, saveRef }) {
  const [allStations, setAllStations] = useState([]);
  const [trackedIds, setTrackedIds] = useState([]);
  const trackedIdsRef = useRef([]);
  const [loading, setLoading] = useState(true);

  const applyTrackedIds = (ids) => {
    trackedIdsRef.current = ids;
    setTrackedIds(ids);
  };

  const fetchStations = async () => {
    try {
      const res = await api.get('/api/trader/tracked-stations');
      const trackedList = res.data.tracked || [];
      const stations = res.data.all_stations || [];
      const merged = mergeTraderStations(trackedList, stations);
      const tracked = merged.filter(s => isTruthyTracked(s.tracked)).map(s => stationIdKey(s.structure_id));
      setAllStations(merged);
      applyTrackedIds(tracked);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchStations(); }, [api]);

  const persistTrackedStations = useCallback(async () => {
    const ids = trackedIdsRef.current.map(id => String(id));
    await api.post('/api/trader/tracked-stations', { station_ids: ids });
    allStations.forEach(s => {
      if (s.margin_enabled || s.broker_fee != null) {
        api.post('/api/trader/tracked-stations/update-margin', {
          structure_id: s.structure_id,
          margin_enabled: s.margin_enabled || false,
          broker_fee: Math.round((parseFloat(s.broker_fee) || 1.0) * 10) / 10,
          kbt_tax: parseFloat(s.kbt_tax) || 0.5,
        });
      }
    });
    const res = await api.get('/api/trader/tracked-stations');
    const trackedList = res.data.tracked || [];
    const stations = res.data.all_stations || [];
    const merged = mergeTraderStations(trackedList, stations);
    const tracked = merged.filter(s => isTruthyTracked(s.tracked)).map(s => stationIdKey(s.structure_id));
    setAllStations(merged);
    applyTrackedIds(tracked);
  }, [api, allStations]);

  useEffect(() => {
    if (!saveRef) return;
    saveRef.current = persistTrackedStations;
    return () => { saveRef.current = null; };
  }, [saveRef, persistTrackedStations]);

  const toggleStation = (sid) => {
    const id = stationIdKey(sid);
    setTrackedIds(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      trackedIdsRef.current = next;
      return next;
    });
  };

  const handleMarginChange = async (sid, field, value) => {
    const id = stationIdKey(sid);
    const rounded = field === 'broker_fee' ? Math.round(value * 10) / 10 : value;
    setAllStations(prev => prev.map(s => stationIdKey(s.structure_id) === id ? { ...s, [field]: rounded } : s));
  };

  if (loading) return <div className="text-zinc-500 text-sm">{t('common.loading')}</div>;

  return (
    <div className="space-y-4">
      <label className="block text-xs font-mono uppercase tracking-widest text-text-muted">
        {t('trader.trackedStations')}
      </label>
      <div className="max-h-80 overflow-y-auto border border-border rounded">
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="text-zinc-400 border-b border-zinc-800 text-xs">
              <th className="w-8 p-2"></th>
              <th className="text-left p-2">{t('trader.station')}</th>
              <th className="text-right p-2">Брокер %</th>
            </tr>
          </thead>
          <tbody>
            {allStations.length === 0 && (
              <tr><td colSpan="3" className="text-center text-zinc-500 italic p-4">{t('trader.nothingFound')}</td></tr>
            )}
            {allStations.map(s => (
              <tr key={s.structure_id} className="border-b border-zinc-800/30 hover:bg-zinc-800/30">
                <td className="p-2 text-center">
                  <input type="checkbox" checked={trackedIds.includes(stationIdKey(s.structure_id))}
                    onChange={() => toggleStation(s.structure_id)}
                    className="accent-cyan-600 w-3 h-3 cursor-pointer" />
                </td>
                <td className="p-2 text-zinc-300 truncate max-w-[200px]">{s.name || s.structure_id}</td>
                <td className="p-2 text-right">
                  <div className="inline-flex items-center gap-0.5">
                    <button onClick={() => handleMarginChange(s.structure_id, 'broker_fee', Math.max(0, Math.round((parseFloat(s.broker_fee ?? 1.0) - 0.1) * 10) / 10))}
                      className="w-5 h-5 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 text-zinc-400 rounded text-[10px] leading-none">&minus;</button>
                    <input type="number" step="0.1" min="0" max="10"
                      value={s.broker_fee ?? 1.0}
                      onChange={e => handleMarginChange(s.structure_id, 'broker_fee', parseFloat(e.target.value) || 0)}
                      className="w-14 bg-zinc-950 border border-zinc-700 px-1.5 py-0.5 text-xs text-white font-mono text-right outline-none focus:border-amber-600 rounded" />
                    <button onClick={() => handleMarginChange(s.structure_id, 'broker_fee', Math.min(10, Math.round((parseFloat(s.broker_fee ?? 1.0) + 0.1) * 10) / 10))}
                      className="w-5 h-5 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 text-zinc-400 rounded text-[10px] leading-none">+</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===== Marginality Sales Tax (Accounting skill) =====
function MarginalitySalesTax({ api, user, salesTax, setSalesTax, accountingLevel, setAccountingLevel }) {
  const [fetching, setFetching] = useState(false);
  const [fetchedCharName, setFetchedCharName] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [selectedCharId, setSelectedCharId] = useState(user?.char_id || null);

  useEffect(() => {
    api.get('/api/characters').then(res => {
      const chars = res.data || [];
      setCharacters(chars);
      if (!selectedCharId && chars.length > 0) {
        setSelectedCharId(chars[0].char_id);
      }
    }).catch(() => {});
  }, []);

  const fetchAccounting = async () => {
    if (!selectedCharId) return;
    setFetching(true);
    try {
      const res = await api.post(`/api/characters/${selectedCharId}/skills/accounting`);
      setAccountingLevel(res.data.accounting_level || 0);
      setSalesTax(res.data.sales_tax != null ? res.data.sales_tax : 7.5);
      setFetchedCharName(res.data.char_name || null);
    } catch (e) { console.error(e); }
    setFetching(false);
  };

  return (
    <div className="bg-zinc-900/50 border border-border p-3 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-xs text-zinc-400">Налог с продажи (навык Accounting)</label>
        <button onClick={fetchAccounting} disabled={fetching || !selectedCharId}
          className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-400 px-2 py-1 rounded font-mono transition-colors disabled:opacity-50 flex items-center gap-1">
          {fetching ? '...' : '⟳'} {fetching ? 'Загрузка...' : 'ESI'}
        </button>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <span className="text-xs text-zinc-500">Персонаж:</span>
          <select value={selectedCharId || ''} onChange={e => { setSelectedCharId(parseInt(e.target.value)); setFetchedCharName(null); }}
            className="bg-zinc-950 border border-zinc-700 px-2 py-0.5 text-xs text-white font-mono outline-none focus:border-cyan-700 rounded">
            {characters.length === 0 && <option value="">—</option>}
            {characters.map(ch => (
              <option key={ch.char_id} value={ch.char_id}>{ch.char_name}{ch.is_main ? ' (осн.)' : ''}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-zinc-500">Уровень:</span>
          <span className="text-sm text-amber-400 font-mono">{accountingLevel}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-zinc-500">Налог:</span>
          <span className="text-sm text-white font-mono">{salesTax.toFixed(2)}%</span>
        </div>
      </div>
      <div className="text-[10px] text-zinc-600 mt-1">Каждый уровень Accounting снижает налог на 11% (7.5% → {Math.max(0, 7.5 - 5 * 0.825).toFixed(2)}% макс)</div>
    </div>
  );
}

// ===== Order Notification Settings =====
function OrderNotifySettings({ t, api, user }) {
  const [notifyTime, setNotifyTime] = useState('12:00');
  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/api/settings/filters');
        setNotifyTime(res.data.order_notify_time || '12:00');
        setNotifyEnabled(res.data.order_notify_enabled || false);
      } catch (e) {}
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/api/settings/filters', { order_notify_time: notifyTime, order_notify_enabled: notifyEnabled });
    } catch (e) {}
    setSaving(false);
  };

  const handleTest = async () => {
    setTestSending(true);
    setTestResult(null);
    try {
      const res = await api.post('/api/settings/send-test-order-notify');
      setTestResult(res.data.status === 'ok' ? 'sent' : 'error');
    } catch (e) {
      setTestResult('error');
    }
    setTestSending(false);
    setTimeout(() => setTestResult(null), 5000);
  };

  return (
    <div className="bg-zinc-900/50 border border-border p-4 rounded-lg">
      <label className="block text-xs font-mono uppercase tracking-widest text-text-muted mb-2">
        {t('settings.orderNotify')}
      </label>
      {!user?.chat_id && (
        <p className="text-xs text-amber-400 mb-2">{t('settings.notifyNoBot')}</p>
      )}
      <div className="flex items-center gap-3 mb-2">
        <input type="time" value={notifyTime} onChange={e => setNotifyTime(e.target.value)}
          className="bg-zinc-950 border border-zinc-700 px-2 py-1 text-sm text-white font-mono outline-none focus:border-cyan-700 rounded-sm" />
        <button onClick={() => setNotifyEnabled(!notifyEnabled)}
          className={`text-xs px-3 py-1.5 rounded font-mono transition-colors ${notifyEnabled ? 'bg-green-700 text-white' : 'bg-zinc-800 text-zinc-400'}`}>
          {notifyEnabled ? t('settings.on') : t('settings.off')}
        </button>
        <button onClick={handleSave} disabled={saving}
          className="text-xs bg-zinc-700 hover:bg-zinc-600 text-zinc-200 px-2 py-1.5 rounded font-mono transition-colors">
          {saving ? t('dashboard.saving') : t('common.save')}
        </button>
        {user?.chat_id && (
          <button onClick={handleTest} disabled={testSending}
            className="text-xs bg-amber-700 hover:bg-amber-600 text-white px-2 py-1.5 rounded font-mono transition-colors">
            {testSending ? '...' : t('settings.sendTestNotify')}
          </button>
        )}
      </div>
      {testResult === 'sent' && (
        <p className="text-xs text-emerald-400 font-mono">✓ Sent</p>
      )}
      {testResult === 'error' && (
        <p className="text-xs text-red-400 font-mono">✗ Failed</p>
      )}
      <p className="text-xs text-zinc-500 font-mono">{t('settings.notifyLocalTime')}</p>
    </div>
  );
}

// --- Остальные страницы (LandingPage, Dashboard) ---
function LandingPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

const handleLogin = () => {
  window.location.href = `${API_URL}/api/auth/login`;
};

const features = [
  { icon: Package, title: 'itemLists', desc: 'itemListsDesc' },
  { icon: MapPin, title: 'stationStock', desc: 'stationStockDesc' },
  { icon: Bell, title: 'notifications', desc: 'notificationsDesc' },
  { icon: FileText, title: 'bulkImport', desc: 'bulkImportDesc' },
  { icon: Share2, title: 'sharing', desc: 'sharingDesc' },
];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="absolute inset-0 opacity-10 bg-cover bg-center" style={{ backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/70abc128-aa7f-4a4c-bb0d-844f54733f24/images/0347c597700302c0d11f30d7d58b21c216463abba28b81bef546b643ce4be942.png)' }} />
      <div className="relative z-10">
        <header className="border-b border-border bg-black/60 backdrop-blur-xl">
          <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Package className="w-6 h-6 text-primary" strokeWidth={1.5} />
              <span className="font-sans font-medium text-xl tracking-tight">EVE Market Tracker</span>
            </div>
            <LanguageSwitcher />
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-24 text-center">
          <div className="space-y-8 animate-fadeIn">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-primary">{t('system.marketMonitoring')}</p>
            <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-white">{t('system.trackMarket')}<br /><span className="text-primary">EVE Online</span></h1>
            <p className="text-base text-text-muted max-w-xl mx-auto">{t('system.createLists')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <button onClick={handleLogin} className="bg-primary text-black font-medium px-8 py-3 hover:bg-primary-hover transition-all flex items-center justify-center gap-2">
                <ExternalLink className="w-4 h-4" /> {t('system.loginEve')}
              </button>
              <a href="https://t.me/EVECheckMarketBot" target="_blank" rel="noopener noreferrer" className="bg-transparent text-white border border-border hover:border-zinc-500 hover:bg-zinc-900 px-8 py-3 transition-all flex items-center justify-center gap-2">
                {t('system.telegramBot')}
              </a>
            </div>
          </div>
        </main>

        <section className="max-w-5xl mx-auto px-6 pb-32">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group bg-black/40 border border-zinc-800/50 hover:border-cyan-800/40 p-6 rounded-sm transition-all duration-500 hover:bg-black/60 hover:shadow-[0_0_30px_-10px_rgba(0,200,200,0.15)] animate-fadeIn"
                  style={{ animationDelay: `${index * 150}ms`, animationFillMode: 'both' }}
                >
                  <div className="w-10 h-10 border border-zinc-800 rounded-sm flex items-center justify-center mb-4 group-hover:border-cyan-700/50 transition-colors duration-500">
                    <Icon className="w-5 h-5 text-zinc-600 group-hover:text-cyan-400 transition-colors duration-500" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-sm font-medium text-zinc-200 group-hover:text-white transition-colors duration-500 mb-2">{t(`landing.${feature.title}`)}</h3>
                  <p className="text-xs text-zinc-600 group-hover:text-zinc-400 leading-relaxed transition-colors duration-500">{t(`landing.${feature.desc}`)}</p>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
} 

function Dashboard() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [stations, setStations] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPackage, setShowNewPackage] = useState(false);
  const [newPackageName, setNewPackageName] = useState('');
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImportText, setBulkImportText] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showShareToast, setShowShareToast] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedStationId, setSelectedStationId] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [errorNotification, setErrorNotification] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [pkgSettingsSaving, setPkgSettingsSaving] = useState(false);
  const [pkgSettingsSavedAt, setPkgSettingsSavedAt] = useState(null);
  const [pkgSettingsError, setPkgSettingsError] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [showRenameHint, setShowRenameHint] = useState(false);
  const [itemsSortBy, setItemsSortBy] = useState('deficit');
  const [itemsSortAsc, setItemsSortAsc] = useState(true);
  const [syncingAll, setSyncingAll] = useState(false);
  const [stationFilter, setStationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [prices, setPrices] = useState({});
  const [siteMode, setSiteMode] = useState(() => localStorage.getItem('site_mode') || user?.site_mode || 'market_list');
  const [featureFlags, setFeatureFlags] = useState({});

  const setMode = async (mode) => {
    setSiteMode(mode);
    localStorage.setItem('site_mode', mode);
    try { await api.post('/api/settings/filters', { site_mode: mode }); } catch {}
  };

  const displayPackages = (() => {
    let list = packages;
    if (stationFilter !== 'all') {
      list = list.filter(p => String(p.station_id) === stationFilter);
    }
    if (statusFilter === 'incomplete') {
      list = list.filter(p => p.items_count > 0 && (p.ready_items || 0) < p.items_count);
    } else if (statusFilter === 'attention') {
      list = list.filter(p => (p.zero_items || 0) > 0);
    } else if (statusFilter === 'complete') {
      list = list.filter(p => p.items_count > 0 && (p.ready_items || 0) >= p.items_count);
    } else if (statusFilter === 'notif_off') {
      list = list.filter(p => !p.notify_enabled);
    } else if (statusFilter === 'notif_on') {
      list = list.filter(p => p.notify_enabled);
    }
    // Always sort by completeness (least ready first)
    list = [...list].sort((a, b) => {
      const aRatio = a.items_count > 0 ? (a.ready_items || 0) / a.items_count : 1;
      const bRatio = b.items_count > 0 ? (b.ready_items || 0) / b.items_count : 1;
      return aRatio - bRatio;
    });
    return list;
  })();

  const hiddenCount = packages.length - displayPackages.length;

  const uniqueStationIds = [...new Set(packages.filter(p => p.station_id).map(p => String(p.station_id)))];
  const stationOptions = uniqueStationIds.map(id => {
    const pkg = packages.find(p => String(p.station_id) === id);
    return { id, name: pkg?.station_name || id };
  });
  const showFilters = packages.length > 3;

  // Reset station filter when selected station no longer exists
  useEffect(() => {
    if (stationFilter !== 'all' && !uniqueStationIds.includes(stationFilter)) {
      setStationFilter('all');
    }
  }, [uniqueStationIds.join(',')]);


  useEffect(() => { loadData(); }, []);
  useEffect(() => { if (selectedPackage) loadItems(selectedPackage.id); }, [selectedPackage]);

  const loadData = async () => {
    try {
      const [pkgRes, stRes] = await Promise.all([
        api.get('/api/packages'),
        api.get('/api/stations')
      ]);
      try {
        const fltRes = await api.get('/api/settings/filters');
        const ff = {
          show_lists: fltRes.data.show_lists !== false,
          show_orders: fltRes.data.show_orders !== false,
          show_trader: fltRes.data.show_trader === true,
        };
        setFeatureFlags(ff);
        const cur = localStorage.getItem('site_mode') || 'market_list';
        const allowed = (cur === 'trader' ? ff.show_trader : cur === 'market_order' ? ff.show_orders : ff.show_lists);
        if (!allowed) {
          const fallback = ff.show_lists ? 'market_list' : 'market_order';
          setSiteMode(fallback);
          localStorage.setItem('site_mode', fallback);
        }
      } catch (e) { console.error('Failed to load feature flags:', e); }
      setPackages(pkgRes.data || []);
      setStations(stRes.data || []);
      if (pkgRes.data?.length > 0 && !selectedPackage) {
        // Select by sort order: least complete first
        const sorted = [...pkgRes.data].sort((a, b) => {
          const aRatio = a.items_count > 0 ? (a.ready_items || 0) / a.items_count : 1;
          const bRatio = b.items_count > 0 ? (b.ready_items || 0) / b.items_count : 1;
          return aRatio - bRatio;
        });
        setSelectedPackage(sorted[0]);
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

const loadItems = async (packageId) => {
  try {
    const response = await api.get(`/api/packages/${packageId}/items`);
    const data = response.data;
    setItems(data);
    if (data.length) {
      const ids = data.map(i => i.type_id).join(',');
      const priceRes = await api.get(`/api/prices?type_ids=${ids}`);
      setPrices(priceRes.data);
    }
  } catch (err) {
    console.error(err);
  }
};

// Get status priority for sorting (lower = more urgent)
// 0: 0 available, 1: <30%, 2: 30-69%, 3: 70-99%, 4: >=100%
const getStatusPriority = (item) => {
  const qty = item.available_qty;
  const multiplier = selectedPackage?.multiplier || 1;
  const needed = item.required_qty * multiplier;

  if (qty === null || qty === undefined) return 5;
  if (qty === 0) return 0;
  const ratio = qty / needed;
  if (ratio >= 5) return 4;   // Много
  if (ratio >= 1) return 3;   // Достаточно
  if (ratio >= 0.7) return 2; // Недостаточно
  if (ratio >= 0.3) return 1; // Мало
  return 0;                   // Критично
};

// Sort function for items table
const getSortedItems = () => {
  if (!items.length) return [];
  return [...items].sort((a, b) => {
    if (itemsSortBy === 'name') {
      return itemsSortAsc ? a.type_name.localeCompare(b.type_name) : b.type_name.localeCompare(a.type_name);
    }
    if (itemsSortBy === 'need') {
      return itemsSortAsc ? a.required_qty - b.required_qty : b.required_qty - a.required_qty;
    }
    if (itemsSortBy === 'have') {
      return itemsSortAsc ? (a.available_qty || 0) - (b.available_qty || 0) : (b.available_qty || 0) - (a.available_qty || 0);
    }
    // Status - default (ascending: 0 first, then 1, 2, 3, 4)
    return itemsSortAsc ? getStatusPriority(a) - getStatusPriority(b) : getStatusPriority(b) - getStatusPriority(a);
  });
};

  const createPackage = async () => {
    if (!newPackageName.trim()) return;
    try {
      await api.post('/api/packages', { 
        name: newPackageName,
        station_id: selectedStationId || null
      });
      setNewPackageName('');
      setSelectedStationId('');
      setShowNewPackage(false);
      loadData();
    } catch (err) { console.error(err); }
  };

  const deletePackage = async (id) => {
    try {
      await api.delete(`/api/packages/${id}`);
      setSelectedPackage(null);
      loadData();
    } catch (err) { console.error(err); }
  };

  const handleBulkImport = async () => {
    if (!bulkImportText.trim()) return;
    setBulkImporting(true);
    try {
      const res = await api.post('/api/packages/bulk-import', {
        text: bulkImportText,
        station_id: selectedStationId || null
      });
      setBulkImportText('');
      setSelectedStationId('');
      setShowBulkImport(false);
      loadData();
      if (res.data.items_not_found?.length > 0) {
        alert(t('import.importedToPackage', { count: res.data.items_added, name: res.data.package_name }) + '\n' + t('import.notFound', { items: res.data.items_not_found.join(', ') }));
      } else {
        alert(t('import.importedToPackage', { count: res.data.items_added, name: res.data.package_name }));
      }
    } catch (err) { 
      console.error(err);
      alert(err?.response?.data?.detail || t('import.importError'));
    } finally {
      setBulkImporting(false);
    }
  };

  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      await api.post('/api/packages/sync-all');
      loadData();
      if (selectedPackage) loadItems(selectedPackage.id);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.detail || t('dashboard.syncError'));
    } finally {
      setSyncingAll(false);
    }
  };

const updatePackage = async (id, data) => {
  const isStationChange = data.station_id !== undefined;
  try {
    const isSettingsUpdate = Object.prototype.hasOwnProperty.call(data, 'check_interval_minutes')
      || Object.prototype.hasOwnProperty.call(data, 'notify_enabled');
    if (isSettingsUpdate) {
      setPkgSettingsSaving(true);
      setPkgSettingsError(null);
    }

    // 1. Отправляем изменения на бэкенд
    await api.put(`/api/packages/${id}`, data);
    
    // 2. Если сменили станцию (и она не null) — синхронизируем пакет (обновит и цены, и наличие)
    if (isStationChange && data.station_id !== null) {
      setSyncError(null);
      setIsSyncing(true);
      setSyncProgress(null);
      const stopPolling = startProgressPolling(id);
      try {
        await api.post(`/api/packages/${id}/sync`);
      } finally {
        stopPolling();
      }
    }
    
    // 3. Обновляем данные
    await loadData();
    await loadItems(id);
    
    // 4. Важно: Обновляем стейт выбранного пакета, чтобы селект переключился визуально
    setSelectedPackage(prev => {
      if (prev?.id === id) {
        return { ...prev, ...data };
      }
      return prev;
    });

    if (isSettingsUpdate) {
      setPkgSettingsSavedAt(Date.now());
    }
  } catch (err) { 
    console.error('Ошибка обновления пакета:', err); 
    const msg = err?.response?.data?.detail || t('dashboard.saveError');
    setPkgSettingsError(msg);
    if (!isStationChange) {
      alert(msg);
    }
  } finally {
    setPkgSettingsSaving(false);
    setIsSyncing(false);
    setSyncProgress(null);
  }
};

  const searchItems = async (query) => {
    if (query.length < 3) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get(`/api/items/search?q=${query}`);
      setSearchResults(res.data);
    } catch (err) {
      console.error("Search error:", err);
    } finally { setSearching(false); }
  };

  useEffect(() => {
    const timer = setTimeout(() => { if (searchQuery) searchItems(searchQuery); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const addItem = async (item) => {
    if (!selectedPackage) return;
    
    // Check if already in package
    const existingIds = items.map(i => i.type_id);
    if (existingIds.includes(item.id)) {
      alert(t('import.alreadyInPackage'));
      return;
    }
    
    try {
      const url = `/api/packages/${selectedPackage.id}/items`;
      
      await api.post(url, {
        type_id: item.id,
        type_name: item.name,
        required_qty: 1
      });

      await loadItems(selectedPackage.id);
      await loadData();
      setShowSearch(false);
    } catch (err) {
      console.error("Ошибка при добавлении:", err.response);
      alert(err.response?.data?.detail || t('import.connectionError'));
    }
  };

const deleteItem = async (itemId) => {
  if (!selectedPackage) return;
  console.log("Удаление предмета с ID:", itemId, "из пакета:", selectedPackage.id);
  try {
    const url = `/api/packages/${selectedPackage.id}/items/${itemId}`;
    console.log("Запрос на удаление к URL:", url);
    
    await api.delete(url);
    
    await loadItems(selectedPackage.id);
    await loadData();
  } catch (err) {
    console.error("Ошибка при удалении:", err.response);
  }
};

const updateItemQty = async (itemId, qty) => {
    if (!selectedPackage) return;
    
    // Оптимистично обновляем стейт, чтобы ввод не "тормозил"
    setItems(prevItems => 
      prevItems.map(it => it.id === itemId ? { ...it, required_qty: qty } : it)
    );

    try {
      // Отправляем изменения на сервер
      await api.put(`/api/packages/${selectedPackage.id}/items/${itemId}`, { 
        required_qty: qty 
      });
      
      // Обновляем общие данные, если нужно пересчитать суммы или счетчики
      await loadData();
    } catch (err) { 
      console.error("Ошибка обновления количества:", err);
      // Если сервер вернул ошибку, откатываем данные к серверным
      loadItems(selectedPackage.id);
    }
  };

const startProgressPolling = (id) => {
  const interval = setInterval(async () => {
    try {
      const res = await api.get(`/api/packages/${id}/sync-progress`);
      setSyncProgress(res.data);
    } catch (_) {}
  }, 600);
  return () => clearInterval(interval);
};


const handleSync = async (id) => {
  // 1. Проверка на дурака
  if (!id || typeof id === 'object') {
    console.warn("Попытка передать объект вместо ID в handleSync");
    return;
  }

  setSyncError(null);
  setIsSyncing(true);
  setSyncProgress(null);

  // Запускаем опрос прогресса
  const stopPolling = startProgressPolling(id);

  try {
    // ВАЖНО: Проверь, что здесь используются ОБРАТНЫЕ КАВЫЧКИ ` а не обычные '
    const response = await api.post(`/api/packages/${id}/sync`);
    stopPolling();
    
    if (response.data && response.data.status === 'error') {
      setSyncError(response.data.message);
    } else {
      // Обновляем и список пакетов, и предметы, чтобы колонка "Есть" обновилась сразу
      await Promise.all([loadData(), loadItems(id)]);
    }
  } catch (err) {
    stopPolling();
    console.error("Sync error:", err);
    // Вытаскиваем сообщение из FastAPI (detail) или нашего JSON (message)
    const msg = err.response?.data?.detail || err.response?.data?.message || t('import.connectionError');
    setSyncError(msg);
  } finally {
    setIsSyncing(false);
    setSyncProgress(null);
  }
};

const handleUpdateQuantity = async (itemId, newQty) => {
  // Превращаем в число, чтобы не было ошибок
  const qty = newQty === '' ? 0 : parseInt(newQty);
  
  // 1. Сразу обновляем локальное состояние, чтобы цифра изменилась в поле
  const updatedItems = items.map(item => 
    item.id === itemId ? { ...item, required_qty: qty } : item
  );
  setItems(updatedItems);

  // 2. Отправляем на сервер
  try {
    await api.put(`/api/items/${itemId}`, { required_qty: qty });
    // После обновления количества на сервере, 
    // желательно обновить список пакетов, чтобы пересчитались цвета (ready/total)
    fetchPackages(); 
  } catch (err) {
    console.error("Ошибка сохранения количества:", err);
  }
};

// --- Внутри функции Dashboard() ---
  if (loading) return <LoadingScreen />;

  // Базовые стили для полей ввода (мягкий акцент при фокусе)
  const inputBaseClasses = "bg-black border border-zinc-800 text-zinc-100 text-sm focus:border-cyan-700/50 focus:ring-1 focus:ring-cyan-900/30 outline-none transition-all duration-150 rounded-sm";

  return (
    <div className="min-h-screen bg-[#060607] text-zinc-300 font-sans selection:bg-cyan-950 selection:text-cyan-100">
      <header className="border-b border-zinc-900 bg-black/40 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-5 py-3 flex justify-between items-center transition-colors">
          <div className="flex items-center gap-3">
            <button className="md:hidden p-2 rounded-md hover:bg-zinc-900 transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <Menu className="w-5 h-5" />
            </button>
            <Package className="w-5 h-5 text-cyan-600" />
            <span className="font-semibold hidden sm:inline tracking-tight text-lg text-zinc-100">EVE Market Tracker</span>
            <div className="ml-3 flex items-center border border-zinc-800 rounded-sm overflow-hidden text-[10px] font-mono">
              {featureFlags.show_lists !== false && (
                <button onClick={() => setMode('market_list')} className={`px-2 py-1 transition-colors ${siteMode === 'market_list' ? 'bg-cyan-800 text-cyan-100' : 'bg-zinc-900 text-zinc-500 hover:text-zinc-300'}`}>{t('dashboard.modeList')}</button>
              )}
              {featureFlags.show_orders !== false && (
                <button onClick={() => setMode('market_order')} className={`px-2 py-1 transition-colors ${siteMode === 'market_order' ? 'bg-cyan-800 text-cyan-100' : 'bg-zinc-900 text-zinc-500 hover:text-zinc-300'}`}>{t('dashboard.modeOrders')}</button>
              )}
              {featureFlags.show_trader === true && (
                <button onClick={() => setMode('trader')} className={`px-2 py-1 transition-colors ${siteMode === 'trader' ? 'bg-cyan-800 text-cyan-100' : 'bg-zinc-900 text-zinc-500 hover:text-zinc-300'}`}>{t('trader.trader')}</button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="text-sm text-zinc-500 hidden sm:inline mr-2 font-mono tracking-tight"
              title={user?.alliance_name || undefined}
            >
              {user?.char_name}
              {user?.corp_ticker ? <span className="text-zinc-600"> [{user.corp_ticker}]</span> : ''}
            </span>
            <LanguageSwitcher />
            <button onClick={() => window.open('https://t.me/EVECheckMarketBot', '_blank')} className={`p-2.5 rounded-md transition-colors ${user?.chat_id ? 'hover:bg-green-900/30' : 'hover:bg-zinc-900'}`} title={user?.chat_id ? t('system.telegramBot') + ' - ' + t('common.settings') : t('system.telegramBot')}>
              <Send className={`w-4 h-4 ${user?.chat_id ? 'text-green-500 hover:text-green-400' : 'text-zinc-600 hover:text-zinc-400'}`} />
            </button>
            <button onClick={() => navigate('/settings')} className="p-2.5 rounded-md hover:bg-zinc-900 transition-colors" title={t('common.settings')}><SettingsIcon className="w-4 h-4 text-zinc-500 hover:text-cyan-400" /></button>
            <div className="relative">
              <button onClick={() => navigate('/stations')} className="p-2.5 rounded-md hover:bg-zinc-900 transition-colors" title={t('common.stations')}>
                <Building2 className="w-4 h-4 text-zinc-500 hover:text-cyan-400" />
              </button>
              {stations.length === 0 && (
                <div className="absolute right-0 top-full mt-2 bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-[11px] text-amber-400 font-mono whitespace-nowrap shadow-xl z-50 animate-fadeIn">
                  <div className="absolute right-3 -top-1.5 w-2 h-2 bg-zinc-800 border-l border-t border-zinc-700 rotate-45" />
                  {t('dashboard.addFirstStation')}
                </div>
              )}
            </div>
            {user?.char_name === 'anton68 Meza' && (
              <button onClick={() => navigate('/admin')} className="p-2.5 rounded-md hover:bg-zinc-900 transition-colors" title="Admin">
                <Users className="w-4 h-4 text-zinc-500 hover:text-cyan-400" />
              </button>
            )}
            <button onClick={logout} className="p-2.5 rounded-md hover:bg-zinc-900 transition-colors text-red-900 hover:text-red-500" title={t('common.logout')}><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      {showBulkImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0f0f11] border border-zinc-800 rounded-sm shadow-2xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <h2 className="text-sm font-semibold text-zinc-100">{t('import.importTitle')}</h2>
              <button onClick={() => { setShowBulkImport(false); setBulkImportText(''); setSelectedStationId(''); }} className="text-zinc-500 hover:text-zinc-300">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <div className="text-xs text-zinc-500 mb-2">{t('import.pasteList')}</div>
              <textarea
                value={bulkImportText}
                onChange={(e) => setBulkImportText(e.target.value)}
                placeholder={t('import.placeholder')}
                className={`${inputBaseClasses} w-full px-3 py-2 mb-3 h-48 font-mono text-xs resize-none`}
                autoFocus
              />
               
              <select 
                className={`${inputBaseClasses} w-full px-3 py-2 mb-4 appearance-none`}
                value={selectedStationId || ''}
                onChange={(e) => setSelectedStationId(e.target.value)}
              >
                <option value="" className="text-zinc-100">{t('dashboard.withoutStation')}</option>
                {stations.map(s => <option key={s.id} value={s.id} className="text-zinc-100 bg-black">{s.name}</option>)}
              </select>

              <div className="flex gap-2.5">
                <button onClick={handleBulkImport} disabled={bulkImporting} className={`flex-1 py-2 rounded-sm text-sm font-semibold transition-colors uppercase tracking-tight ${bulkImporting ? 'bg-cyan-800 text-cyan-400 cursor-wait' : 'bg-cyan-700 text-cyan-950 hover:bg-cyan-600'}`}>
                  {bulkImporting ? t('import.importing') : t('import.importItems')}
                </button>
                <button onClick={() => { setShowBulkImport(false); setBulkImportText(''); setSelectedStationId(''); }} disabled={bulkImporting} className="flex-1 border border-zinc-800 text-zinc-400 py-2 rounded-sm text-sm hover:bg-zinc-900 transition-colors disabled:opacity-50">{t('common.cancel')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {siteMode === 'market_order' ? (
        <OrderChecker user={user} t={t} api={api} stations={stations} />
      ) : siteMode === 'trader' ? (
        <TraderPage />
      ) : (
        <>
      <div className="max-w-7xl mx-auto flex">
        {/* Боковая панель: Пакеты */}
        <aside className={`${mobileMenuOpen ? 'block' : 'hidden'} md:block w-full md:w-72 border-r border-zinc-900 bg-[#0a0a0b] min-h-[calc(100vh-57px)] fixed md:sticky top-[57px] z-40 transition-all duration-300`}>
          <div className="p-5">
            <div className="mb-5">
              <div className="flex flex-col gap-1.5">
                <button onClick={() => { setShowBulkImport(false); setShowNewPackage(true); }} className="text-[10px] px-3 py-1.5 rounded bg-cyan-900/40 hover:bg-cyan-800/60 text-cyan-400 hover:text-cyan-300 transition-colors border border-cyan-800/40 inline-flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" /> {t('dashboard.addList')}
                </button>
                <button onClick={() => { setShowNewPackage(false); setShowBulkImport(true); }} className="text-[10px] px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors inline-flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> {t('dashboard.importItems')}
                </button>
                <button onClick={handleSyncAll} disabled={syncingAll} className="text-[10px] px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50">
                  <RefreshCw className={`w-3.5 h-3.5 ${syncingAll ? 'animate-spin' : ''}`} /> {syncingAll ? t('dashboard.updating') : t('dashboard.updateAllLists')}
                </button>
              </div>
            </div>

            <div className="mb-3">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-100">{t('dashboard.myLists')}</div>
              <div className="text-[11px] font-mono text-zinc-400 mt-0.5">
                {t('dashboard.found')} {packages.length}
                {hiddenCount > 0 && <span className="text-zinc-500">, {t('dashboard.hidden2')} {hiddenCount}</span>}
              </div>
            </div>

            {showFilters && (
              <div className="mb-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">{t('dashboard.filter')}</div>
                <div className="space-y-2">
                  <select
                    value={stationFilter}
                    onChange={(e) => setStationFilter(e.target.value)}
                    className="w-full bg-zinc-900 border border-zinc-800 text-zinc-300 text-[11px] font-mono px-2 py-1.5 rounded outline-none focus:border-cyan-800 appearance-none"
                  >
                    <option value="all">{t('dashboard.allStations')}</option>
                    {stationOptions.map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.name}</option>
                    ))}
                  </select>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-800 text-zinc-300 text-[11px] font-mono px-2 py-1.5 rounded outline-none focus:border-cyan-800 appearance-none"
                >
                  <option value="all">{t('dashboard.allLists')}</option>
                  <option value="incomplete">{t('dashboard.filterIncomplete')}</option>
                  <option value="attention">{t('dashboard.filterAttention')}</option>
                  <option value="complete">{t('dashboard.filterComplete')}</option>
                  <option value="notif_off">{t('dashboard.filterNotifOff')}</option>
                  <option value="notif_on">{t('dashboard.filterNotifOn')}</option>
                </select>
              </div>
            </div>
            )}
            
            {showNewPackage && (
              <div className="mb-5 border border-zinc-800 p-4 bg-black rounded-sm shadow-xl animate-slideInDown">
                <input 
                  type="text" 
                  value={newPackageName} 
                  onChange={(e) => setNewPackageName(e.target.value)} 
                  placeholder={t('dashboard.packageName')} 
                  className={`${inputBaseClasses} w-full px-3 py-2 mb-2.5`}
                  autoFocus 
                />
                
                <select 
                  className={`${inputBaseClasses} w-full px-3 py-2 mb-3.5 appearance-none`}
                  value={selectedStationId || ''}
                  onChange={(e) => setSelectedStationId(e.target.value)}
                >
                  <option value="" className="text-zinc-100">{t('dashboard.withoutStation')}</option>
                  {stations.map(s => <option key={s.id} value={s.id} className="text-zinc-100 bg-black">{s.name}</option>)}
                </select>

                <div className="flex gap-2.5">
                  <button onClick={createPackage} className="flex-1 bg-cyan-700 text-cyan-950 py-2 rounded-sm text-sm font-semibold hover:bg-cyan-600 transition-colors uppercase tracking-tight">{t('dashboard.create')}</button>
                  <button onClick={() => { setShowNewPackage(false); setSelectedStationId(''); }} className="flex-1 border border-zinc-800 text-zinc-400 py-2 rounded-sm text-sm hover:bg-zinc-900 transition-colors">{t('common.cancel')}</button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {displayPackages.map(pkg => {
                // 1. Считаем проценты для цвета
                const total = pkg.items_count || 0;
                const ready = pkg.ready_items || 0;
                const ratio = total > 0 ? ready / total : 0;

                // 2. Логика цвета фона
                let badgeStyle = "bg-zinc-900 text-zinc-500"; // По умолчанию серый
                if (total > 0) {
                  if ((pkg.zero_items || 0) > 0) badgeStyle = "bg-red-600/20 text-red-400 border border-red-500/30";
                  else if (ratio >= 1) badgeStyle = "bg-green-600/20 text-green-400 border border-green-500/30"; 
                  else if (ratio >= 0.5) badgeStyle = "bg-yellow-600/20 text-yellow-500 border border-yellow-500/30";
                  else badgeStyle = "bg-red-900/30 text-red-500 border border-red-500/30";
                }

                return (
                  <div 
                    key={pkg.id} 
                    className={`p-4 cursor-pointer transition-all duration-150 group rounded-sm shadow-inner
                      ${selectedPackage?.id === pkg.id 
                        ? 'bg-[#121215] border border-cyan-800/40 shadow-cyan-950/20' 
                        : 'bg-black border border-zinc-900/50 hover:border-zinc-800 hover:bg-[#0c0c0d]'}`} 
                    onClick={() => {
                      setSelectedPackage(pkg);
                      setMobileMenuOpen(false);
                    }}
                  >
                    <div className={`text-sm font-medium truncate ${selectedPackage?.id === pkg.id ? 'text-cyan-300' : 'text-zinc-200'}`}>
                      {pkg.name}
                    </div>
                    
                    <div className="flex justify-between items-center mt-2.5">
                      <div className="text-[11px] text-zinc-500 truncate mr-3 font-mono flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-zinc-500" />
                        {pkg.station_name || t('dashboard.stationNotSelected')}
                      </div>

                      {/* Исправленный счетчик (ready_items / items_count) */}
                      <div className={`text-[10px] px-2 py-0.5 rounded-sm shrink-0 font-mono font-bold ${badgeStyle}`}>
                        {ready}/{total}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </aside>

        {/* Основная область: Предметы пакета */}
        <main className="flex-1 p-5 sm:p-8 bg-[#060607]">
          {selectedPackage ? (
            <div className="animate-fadeIn">
             <div className="border border-zinc-900 bg-black p-5 mb-6 flex flex-col gap-6 rounded-sm shadow-xl">
                {/* ВЕРХНЯЯ ПАНЕЛЬ: Иконка, Название и Кнопка в одну строку */}
                <div className="flex items-center justify-between w-full gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-zinc-900/50 border border-zinc-800 rounded-sm flex items-center justify-center shrink-0">
                      <Package className="w-6 h-6 text-cyan-800" strokeWidth={1.5} />
                    </div>
                    {isEditingName ? (
                      <input
                        type="text"
                        value={editingNameValue}
                        onChange={(e) => setEditingNameValue(e.target.value)}
                        onBlur={() => {
                          if (editingNameValue.trim()) {
                            updatePackage(selectedPackage.id, { name: editingNameValue.trim() });
                          }
                          setIsEditingName(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            if (editingNameValue.trim()) {
                              updatePackage(selectedPackage.id, { name: editingNameValue.trim() });
                            }
                            setIsEditingName(false);
                          }
                          if (e.key === 'Escape') {
                            setEditingNameValue(selectedPackage.name);
                            setIsEditingName(false);
                          }
                        }}
                        className="text-2xl font-light tracking-tight text-zinc-100 leading-tight bg-transparent border-b border-cyan-500 outline-none"
                        autoFocus
                        title={t('dashboard.enterToSave')}
                      />
                    ) : (
                      <div>
                        <div className="relative" onMouseEnter={() => setShowRenameHint(true)} onMouseLeave={() => setShowRenameHint(false)}>
                          <h2 
                            className="text-2xl font-light tracking-tight text-zinc-100 leading-tight cursor-pointer hover:text-cyan-400 transition-colors"
                            onClick={() => {
                              setEditingNameValue(selectedPackage.name);
                              setIsEditingName(true);
                            }}
                          >
                            {selectedPackage.name}
                          </h2>
                          {showRenameHint && (
                            <div className="absolute left-0 top-full mt-1 px-2 py-1 bg-zinc-800 text-zinc-300 text-xs rounded whitespace-nowrap z-50">
                              {t('dashboard.renameHint')}
                            </div>
                          )}
                        </div>
                        {(() => {
                          const total = items.reduce((sum, i) => {
                            const price = i.sell_price || prices[i.type_id];
                            return price ? sum + price * i.required_qty : sum;
                          }, 0);
                          if (items.length === 0) return null;
                          return (
                            <p className="text-[11px] text-zinc-500 font-mono mt-1" title={t('dashboard.priceWithoutMultiplier')}>
                              x1 {t('dashboard.totalPrice')} ≈ {total > 0 ? total.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'} ISK
                            </p>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={async () => {
                        try {
                          const res = await api.post(`/api/packages/${selectedPackage.id}/share`);
                          await navigator.clipboard.writeText(res.data.url);
                          setShowShareToast(true);
                          setTimeout(() => setShowShareToast(false), 3000);
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                      className="shrink-0 p-2 rounded-sm border border-zinc-800 text-zinc-500 hover:text-cyan-400 hover:border-cyan-500/50 transition-colors"
                      title={t('dashboard.sharePackage')}
                    >
                      <Share2 className="w-4 h-4" />
                    </button>

                    <div className="shrink-0">
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={selectedPackage.multiplier || 1}
                        onChange={(e) => updatePackage(selectedPackage.id, { multiplier: parseInt(e.target.value) || 1 })}
                        className="w-12 px-2 py-2 rounded-sm bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs font-mono text-center outline-none hover:border-cyan-800/50 focus:border-cyan-800/50 transition-colors"
                        title={t('dashboard.multiplier')}
                        placeholder="x1"
                      />
                    </div>

                    <button
                      onClick={() => handleSync(selectedPackage.id)}
                      disabled={isSyncing}
                      className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-sm border text-[11px] font-mono uppercase tracking-[0.15em] transition-all
                        ${isSyncing
                          ? 'border-cyan-800/40 text-cyan-300/80 animate-pulse'
                          : 'border-zinc-800 text-zinc-400 hover:text-cyan-300 hover:border-cyan-800/50 active:scale-[0.99]'}`}
                      title={t('dashboard.updateMarket')}
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                      <span className="leading-none">
                        {isSyncing ? t('dashboard.updating') : t('dashboard.updateMarket')}
                      </span>
                    </button>

                    <button
                      onClick={() => {
                        if (confirm(t('confirm.deletePackage', { name: selectedPackage.name }))) {
                          deletePackage(selectedPackage.id);
                        }
                      }}
                      className="shrink-0 p-2 rounded-sm border border-zinc-800 text-zinc-500 hover:text-red-400 hover:border-red-500/50 transition-colors"
                      title={t('dashboard.deletePackage')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Прогресс синхронизации */}
                {syncProgress && (
                  <div className="pt-3 pb-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-zinc-500 font-mono truncate">
                        {syncProgress.message}
                      </span>
                      <span className="text-[10px] text-cyan-500/80 font-mono tabular-nums ml-3">
                        {syncProgress.percent}%
                      </span>
                    </div>
                    <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-cyan-700 to-cyan-500 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${syncProgress.percent}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* НИЖНЯЯ ПАНЕЛЬ: Отдельные блоки настроек */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-zinc-900">
                  
                  {/* Блок Локации */}
                  <div className="flex items-center gap-3 p-3 bg-[#0c0c0d] border border-zinc-800 rounded-sm">
                    <MapPin className="w-4 h-4 text-cyan-700" />
                    <div className="flex flex-col w-full">
                      <span className="text-[9px] text-zinc-500 uppercase font-mono tracking-wider leading-none mb-1">{t('dashboard.currentLocation')}</span>
                      <select
                        value={selectedPackage.station_id || ''}
                        onChange={(e) => updatePackage(selectedPackage.id, { station_id: e.target.value ? parseInt(e.target.value) : null })}
                        className="bg-transparent text-sm text-zinc-100 outline-none focus:text-primary transition-colors w-full appearance-none cursor-pointer"
                      >
                        <option value="" className="bg-zinc-900 text-zinc-500">{t('dashboard.stationNotSelected')}</option>
                        {stations.map(s => (
                          <option key={s.id} value={s.id} className="bg-zinc-900 text-zinc-100">
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-[#0c0c0d] border border-zinc-800 rounded-sm">
                    <Bell className="w-4 h-4 text-cyan-700" />
                    <div className="flex flex-col w-full">
                      <span className="text-[9px] text-zinc-500 uppercase font-mono tracking-wider leading-none mb-1">{t('dashboard.telegramNotifications')}</span>
                      <div className="flex items-center justify-between gap-3">
                        <select
                          value={selectedPackage.check_interval_minutes ?? 60}
                          onChange={(e) => updatePackage(selectedPackage.id, { check_interval_minutes: parseInt(e.target.value) })}
                          className="bg-transparent text-sm text-zinc-100 outline-none focus:text-primary transition-colors appearance-none cursor-pointer flex-1"
                          title={t('dashboard.telegramNotifications')}
                        >
                <option value="60"   className="bg-zinc-900 text-zinc-100">{t('dashboard.every1hour')}</option>
                <option value="360"  className="bg-zinc-900 text-zinc-100">{t('dashboard.every6hours')}</option>
                <option value="720"  className="bg-zinc-900 text-zinc-100">{t('dashboard.every12hours')}</option>
                <option value="2880" className="bg-zinc-900 text-zinc-100">{t('dashboard.every2days')}</option>
                <option value="10080" className="bg-zinc-900 text-zinc-100">{t('dashboard.everyWeek')}</option>
                        </select>

                        <button
                          onClick={() => updatePackage(selectedPackage.id, { notify_enabled: !selectedPackage.notify_enabled })}
                          disabled={pkgSettingsSaving}
                          className={`px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider border transition-colors shrink-0
                            ${selectedPackage.notify_enabled === false
                              ? 'border-zinc-700 text-zinc-500 hover:text-zinc-300 hover:border-zinc-500'
                              : 'border-cyan-800/60 text-cyan-300 hover:border-cyan-600/80'}
                            ${pkgSettingsSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={t('dashboard.telegramNotifications')}
                        >
                          {selectedPackage.notify_enabled === false ? t('dashboard.notificationsOff') : t('dashboard.notificationsOn')}
                        </button>
                      </div>
                       
                      {/* Статус сохранения (вынесен в абсолют или просто вниз) */}
                      <div className="h-2 mt-1">
                        {pkgSettingsSaving ? (
                          <span className="text-[10px] text-zinc-600 font-mono">{t('dashboard.saving')}</span>
                        ) : pkgSettingsError ? (
                          <span className="text-[10px] text-red-500/80 font-mono">{pkgSettingsError}</span>
                        ) : pkgSettingsSavedAt ? (
                          <span className="text-[10px] text-zinc-700 font-mono">{t('dashboard.saved')}</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {showShareToast && (
                <div className="mb-6 p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-lg flex items-center gap-3 text-cyan-200 animate-in fade-in slide-in-from-top-2">
                  <Check className="w-5 h-5 text-cyan-500 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{t('notifications.linkCopied')}</p>
                  </div>
                </div>
              )}

              {syncError && (
                <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-3 text-amber-200 animate-in fade-in slide-in-from-top-2">
                  <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{syncError || t('notifications.syncError')}</p>
                  </div>
                  <button 
                    onClick={() => setSyncError(null)}
                    className="p-1 hover:bg-amber-500/20 rounded-md transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              <div className="border border-zinc-900 bg-black overflow-x-auto rounded-sm shadow-2xl">
                <table className="w-full text-left min-w-[600px]">
                  <thead>
                    <tr className="border-b border-zinc-800 bg-[#0c0c0d] text-zinc-200">
                      <th className="p-4 text-xs uppercase tracking-wider font-mono cursor-pointer hover:text-cyan-400 transition-colors" onClick={() => { setItemsSortBy('name'); setItemsSortAsc(!itemsSortAsc); }}>
                        {t('items.item')} {itemsSortBy === 'name' && (itemsSortAsc ? '↑' : '↓')}
                      </th>
                      <th className="p-4 text-xs uppercase tracking-wider text-center font-mono cursor-pointer hover:text-cyan-400 transition-colors" onClick={() => { setItemsSortBy('need'); setItemsSortAsc(!itemsSortAsc); }}>
                        {t('items.need')} {itemsSortBy === 'need' && (itemsSortAsc ? '↑' : '↓')}
                      </th>
                      <th className="p-4 text-xs uppercase tracking-wider text-center font-mono cursor-pointer hover:text-cyan-400 transition-colors" onClick={() => { setItemsSortBy('have'); setItemsSortAsc(!itemsSortAsc); }}>
                        {t('items.have')} {itemsSortBy === 'have' && (itemsSortAsc ? '↑' : '↓')}
                      </th>
                      <th className="p-4 text-xs uppercase tracking-wider text-center font-mono cursor-pointer hover:text-cyan-400 transition-colors" onClick={() => { setItemsSortBy('deficit'); setItemsSortAsc(!itemsSortAsc); }}>
                        {t('items.status')} {itemsSortBy === 'deficit' && (itemsSortAsc ? '↑' : '↓')}
                      </th>
                      <th className="p-4 w-14"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-900/40">
                    {getSortedItems().map(item => (
                      <tr key={item.id} className="hover:bg-zinc-950 transition-colors group">
                         <td className="p-4 text-sm font-medium text-zinc-200 flex items-center gap-3">
                           <div className="w-8 h-8 bg-zinc-900/50 border border-zinc-800 rounded-sm flex items-center justify-center shrink-0">
                             <Package className="w-4 h-4 text-zinc-200" strokeWidth={1}/>
                           </div>
                           <div className="flex flex-col">
                             <button
                               onClick={() => { navigator.clipboard.writeText(item.type_name); }}
                               className="leading-tight text-left hover:text-cyan-400 transition-colors cursor-pointer"
                               title={t('items.copyName')}
                             >
                               {item.type_name}
                             </button>
                             <span className="text-[11px] text-zinc-500 font-mono mt-0.5">
                               {t('items.id')}: {item.type_id}
                             </span>
                           </div>
                         </td>
                        <td className="p-4 text-center">
                          <input
                            type="number"
                            min="0"
                            value={item.required_qty || 0}
                            onChange={(e) => handleUpdateQuantity(item.id, e.target.value)}
                            className="w-20 bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-sm text-zinc-200 focus:outline-none focus:border-cyan-500/50 transition-colors"
                          />
                          {selectedPackage.multiplier > 1 && <span className="text-[11px] text-cyan-500 ml-1">×{selectedPackage.multiplier}</span>}
                        </td>
                        <td className="p-4 text-center font-mono text-base text-zinc-400">{item.available_qty ?? '—'}</td>
                        <td className="p-4 text-center">
                          {item.available_qty !== null ? (() => {
                            const multiplier = selectedPackage.multiplier || 1;
                            const needed = item.required_qty * multiplier;
                            const ratio = item.available_qty / needed;

                             // Много: >= 500%
                             if (ratio >= 5) {
                               return (
                                 <div className="inline-flex items-center gap-1.5 text-cyan-400 bg-cyan-500/5 px-2.5 py-1 rounded-full text-xs font-medium border border-cyan-900/40">
                                   <Check className="w-3.5 h-3.5" /> {t('items.plenty')}
                                 </div>
                               );
                             }

                             // Достаточно: 100% - 499%
                             if (ratio >= 1) {
                               return (
                                 <div className="inline-flex items-center gap-1.5 text-green-400 bg-green-500/5 px-2.5 py-1 rounded-full text-xs font-medium border border-green-900/40">
                                   <Check className="w-3.5 h-3.5" /> {t('items.enough')}
                                 </div>
                               );
                             }

                             // Недостаточно: 70% - 99%
                             if (ratio >= 0.7) {
                               return (
                                 <div className="inline-flex items-center gap-1.5 text-yellow-400 bg-yellow-500/5 px-2.5 py-1 rounded-full text-xs font-medium border border-yellow-900/40">
                                   <AlertCircle className="w-3.5 h-3.5" /> {t('items.insufficient')}
                                 </div>
                               );
                             }

                             // Мало: 30% - 69%
                             if (ratio >= 0.3) {
                               return (
                                 <div className="inline-flex items-center gap-1.5 text-red-400 bg-red-500/5 px-2.5 py-1 rounded-full text-xs font-medium border border-red-900/40">
                                   <AlertCircle className="w-3.5 h-3.5" /> {t('items.low')}
                                 </div>
                               );
                             }

                             // Критично / 0: < 30%
                              return (
                                <div className="inline-flex items-center gap-1.5 text-red-600 bg-red-600/5 px-2.5 py-1 rounded-full text-xs font-bold border border-red-900/60">
                                  <X className="w-3.5 h-3.5" /> {t('items.critical')}
                                </div>
                              );
                          })() : (
                            <span className="text-zinc-700 font-mono">—</span>
                          )}
                        </td>
                         <td className="p-4 text-right">
                           <button 
                             onClick={() => deleteItem(item.id)} 
                             className="text-red-900 hover:text-red-500 transition-colors p-2 rounded-md hover:bg-red-950/20"
                             title={t('common.delete')}
                           >
                             <X className="w-4 h-4" />
                           </button>
                         </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                 <button onClick={() => setShowSearch(true)} className="w-full p-4 text-cyan-700 hover:text-cyan-400 border-t border-zinc-800 hover:bg-black/50 flex justify-center gap-2.5 items-center transition-colors text-sm uppercase tracking-wider font-semibold">
                   <Plus className="w-4 h-4" /> {t('common.search')}
                 </button>
              </div>
            </div>
          ) : (
            <div className="py-32 text-center text-zinc-100 bg-black rounded-sm border border-zinc-900 shadow-inner">
              <Package className="w-16 h-16 mx-auto mb-6 opacity-10 text-cyan-600" strokeWidth={1} />
              <p className="font-mono text-sm uppercase tracking-[0.2em]">{t('dashboard.selectPackage')}</p>
              <p className="text-xs mt-2 text-zinc-700">{t('dashboard.createNew')} <span className="text-cyan-800 inline-block align-middle"><Plus className="w-3 h-3"/></span></p>
            </div>
          )}
        </main>
      </div>

      {/* Модальное окно поиска (тоже стилизовано) */}
      {showSearch && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fadeIn" onClick={() => setShowSearch(false)}>
          <div className="bg-[#0c0c0d] border border-zinc-800 w-full max-w-xl p-7 rounded-sm shadow-2xl animate-scaleIn" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-light text-zinc-100 tracking-tight flex items-center gap-3">
                <Search className="w-5 h-5 text-cyan-800" /> {t('common.search')} (ESI)
              </h3>
              <button onClick={() => setShowSearch(false)} className="p-1.5 rounded-md hover:bg-zinc-800 text-zinc-100 hover:text-white transition-colors"><X className="w-5 h-5"/></button>
            </div>
            <input 
              type="text" 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              placeholder={t('dashboard.searchPlaceholder')} 
              className={`${inputBaseClasses} w-full px-4 py-3 text-base mb-6 focus:ring-cyan-900/40`} 
              autoFocus 
            />
<div className="max-h-80 overflow-y-auto space-y-1.5 pr-2 -mr-2 scrollbar-thin">
              {searching ? (
                <div className="text-center py-10 font-mono text-cyan-600 animate-pulse text-sm uppercase tracking-widest">Searching ESI Database...</div>
              ) : (searchResults && Array.isArray(searchResults) && searchResults.length > 0) ? (
                searchResults.map(it => (
                  <button 
                    key={it.id} 
                    onClick={() => addItem(it)} 
                    className="w-full text-left p-3.5 hover:bg-black border border-zinc-800 hover:border-cyan-900 rounded-sm text-sm flex justify-between items-center transition-all group"
                  >
                    <span className="text-zinc-200 group-hover:text-cyan-300 transition-colors">{it.name}</span>
                    <span className="text-zinc-700 font-mono text-xs tracking-tighter transition-colors group-hover:text-zinc-500">ID: {it.id}</span>
                  </button>
                ))
              ) : (searchQuery.length >= 3 && (!searchResults || searchResults.length === 0)) && (
                 <div className="text-center py-10 text-zinc-700 text-sm italic">{t('dashboard.noResults')} "{searchQuery}"</div>
              )}
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
   );
function OrderChecker({ user, t, api, stations }) {
  const [orders, setOrders] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [lastCheck, setLastCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Map());
  const [focusedRow, setFocusedRow] = useState(null);
  const navigate = useNavigate();

  // Persisted cooldown via localStorage (survives page refresh)
  const COOLDOWN_KEY = 'order_check_cooldown_end';
  const getCooldown = () => {
    const end = parseInt(localStorage.getItem(COOLDOWN_KEY), 10);
    if (!end) return 0;
    return Math.max(0, Math.floor((end - Date.now()) / 1000));
  };
  const [checkCooldown, setCheckCooldown] = useState(getCooldown);
  const [historyOrders, setHistoryOrders] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('sell');

  useEffect(() => {
    if (checkCooldown <= 0) return;
    const id = setInterval(() => {
      const remaining = getCooldown();
      setCheckCooldown(remaining);
      if (remaining <= 0) localStorage.removeItem(COOLDOWN_KEY);
    }, 1000);
    return () => clearInterval(id);
  }, [checkCooldown]);

  const CATEGORY_LABELS = {
    'Favorites': '⭐ ' + t('dashboard.favorites'),
    'Ammo': t('dashboard.catAmmo'),
    'Ships': t('dashboard.catShips'),
    'Modules': t('dashboard.catModules'),
    'Drones': t('dashboard.catDrones'),
    'Implants': t('dashboard.catImplants'),
    'Resources': t('dashboard.catResources'),
    'Other': t('dashboard.catOther'),
  };

  const CATEGORY_ORDER = ['Favorites', 'Ammo', 'Ships', 'Modules', 'Drones', 'Implants', 'Resources', 'Other'];

  const loadOrders = async () => {
    try {
      const res = await api.get('/api/orders');
      setOrders(res.data.orders || []);
      setCharacters(res.data.characters || []);
      setLastCheck(res.data.last_check);
      const favRes = await api.get('/api/orders/favorites');
      setFavorites(new Set((favRes.data || []).map(f => f.type_id)));
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadOrders(); }, []);

  const handleCheck = async () => {
    if (checking || checkCooldown > 0) return;
    setChecking(true);
    try {
      const res = await api.post('/api/orders/check');
      localStorage.setItem(COOLDOWN_KEY, String(Date.now() + 1800000));
      setCheckCooldown(1800);
      await loadOrders();
    } catch (e) { console.error(e); }
    setChecking(false);
  };

  const loadHistory = async (pageNum, filter) => {
    if (historyLoading) return;
    setHistoryLoading(true);
    try {
      const res = await api.get(`/api/orders/history?page=${pageNum}&per_page=50&order_type=${filter || historyFilter}`);
      if (pageNum === 1) {
        setHistoryOrders(res.data.orders || []);
      } else {
        setHistoryOrders(prev => [...prev, ...(res.data.orders || [])]);
      }
      setHistoryTotal(res.data.total);
      setHistoryPage(pageNum);
    } catch (e) { console.error(e); }
    setHistoryLoading(false);
  };

  const toggleHistory = () => {
    if (!historyVisible) {
      setHistoryVisible(true);
      setHistoryPage(0);
      setHistoryOrders([]);
      loadHistory(1, historyFilter);
    } else {
      setHistoryVisible(false);
      setHistoryOrders([]);
      setHistoryPage(0);
    }
  };

  const setFilter = (f) => {
    setHistoryFilter(f);
    setHistoryOrders([]);
    setHistoryPage(0);
    setHistoryTotal(0);
    loadHistory(1, f);
  };

  const toggleFavorite = async (typeId, typeName) => {
    try {
      if (favorites.has(typeId)) {
        await api.delete(`/api/orders/favorites/${typeId}`);
        const newFav = new Set(favorites);
        newFav.delete(typeId);
        setFavorites(newFav);
      } else {
        await api.post('/api/orders/favorites', { type_id: typeId, type_name: typeName });
        setFavorites(new Set([...favorites, typeId]));
      }
    } catch (e) { console.error(e); }
  };

  const copyName = (name) => {
    navigator.clipboard.writeText(name).catch(() => {});
  };

  // Group orders by category — favorited items only in Favorites, removed from their original category
  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    let items;
    if (cat === 'Favorites') {
      items = orders.filter(o => favorites.has(o.type_id));
    } else {
      items = orders.filter(o => o.category_name === cat && !favorites.has(o.type_id));
    }
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});

  // Count attention-needed orders (non-first sell orders that aren't blocked)
  const attentionCount = (cat) => {
    return (grouped[cat] || []).filter(o => !o.is_buy_order && !o.is_first && !o.is_blocked_hub).length;
  };

  const totalAttention = CATEGORY_ORDER.reduce((s, cat) => s + attentionCount(cat), 0);

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => {
      const next = new Map(prev);
      next.set(cat, !next.get(cat));
      return next;
    });
  };

  const formatPrice = (p) => {
    if (!p || p === 0) return '—';
    const s = p.toFixed(2);
    const [intPart, decPart] = s.split('.');
    const spaced = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');
    return `${spaced}.${decPart}`;
  };

  const pctDiff = (myPrice, jitaPrice) => {
    if (!jitaPrice || jitaPrice === 0) return null;
    return ((myPrice - jitaPrice) / jitaPrice * 100).toFixed(1);
  };

  return (
    <div className="max-w-7xl mx-auto p-5">
      {/* Header bar */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-zinc-100">{t('dashboard.ordersTitle')}</h2>
          <p className="text-xs text-zinc-500 font-mono">
            {characters.length > 0 && `${t('dashboard.ordersChars')}: ${characters.map(c => c.char_name).join(', ')}`}
            {lastCheck && ` | ${t('dashboard.lastCheck')}: ${new Date(lastCheck).toUTCString()}`}
          </p>
          {orders.length > 0 && (
            <p className="text-xs text-zinc-500 mt-1">
              {t('dashboard.ordersTotal')}: {orders.length} | {t('dashboard.ordersAttention')}: <span className="text-red-400">{totalAttention}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleCheck} disabled={checking || checkCooldown > 0}
            className="text-xs bg-cyan-800 hover:bg-cyan-700 text-cyan-100 px-3 py-1.5 rounded font-mono transition-colors disabled:opacity-50">
            {checking ? t('dashboard.checking') : checkCooldown > 0
              ? `${t('dashboard.checkWait')} ${Math.floor(checkCooldown / 60)}:${String(checkCooldown % 60).padStart(2, '0')}`
              : t('dashboard.checkOrders')}
          </button>
          <button onClick={toggleHistory} className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-400 px-3 py-1.5 rounded font-mono transition-colors">
            {historyVisible ? t('dashboard.historyHide') : t('dashboard.historyShow') + (historyTotal > 0 ? ` (${historyTotal})` : '')}
          </button>
          <button onClick={() => navigate('/settings')} className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-400 px-3 py-1.5 rounded font-mono transition-colors">
            {t('common.settings')}
          </button>
        </div>
      </div>

      {/* Order History */}
      {historyVisible && (
        <div className="mb-6 border border-zinc-800 rounded-sm overflow-hidden">
          <div className="px-4 py-2.5 bg-zinc-900/50 flex items-center justify-between">
            <span className="text-sm text-zinc-300 font-mono">{t('dashboard.historyTitle')} ({historyTotal})</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setFilter('all')} className={`text-xs px-2 py-1 rounded font-mono ${historyFilter === 'all' ? 'bg-zinc-700 text-zinc-200' : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300'}`}>All</button>
              <button onClick={() => setFilter('sell')} className={`text-xs px-2 py-1 rounded font-mono ${historyFilter === 'sell' ? 'bg-green-800 text-green-200' : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300'}`}>Sell</button>
              <button onClick={() => setFilter('buy')} className={`text-xs px-2 py-1 rounded font-mono ${historyFilter === 'buy' ? 'bg-blue-800 text-blue-200' : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300'}`}>Buy</button>
              {historyLoading && <span className="text-xs text-zinc-500">{t('common.loading')}</span>}
            </div>
          </div>
          {(() => {
            const filtered = historyOrders.filter(o => historyFilter === 'all' ? true : historyFilter === 'sell' ? !o.is_buy_order : o.is_buy_order);
            const fmtShort = (v) => { if (!v) return '—'; if (v >= 1000000000) return (v/1000000000).toFixed(1)+'B'; if (v >= 1000000) return (v/1000000).toFixed(1)+'M'; if (v >= 1000) return (v/1000).toFixed(1)+'K'; return v.toFixed(0); };
            return filtered.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-zinc-500 font-mono uppercase tracking-wider border-b border-zinc-800">
                    <th className="p-2 text-left">{t('dashboard.colName')}</th>
                    <th className="p-2 text-left">{t('dashboard.colChar')}</th>
                    <th className="p-2 text-center">B/S</th>
                    <th className="p-2 text-right">{t('dashboard.colPrice')}</th>
                    <th className="p-2 text-right">Jita</th>
                    <th className="p-2 text-right">Profit</th>
                    <th className="p-2 text-right">{t('dashboard.colQty')}</th>
                    <th className="p-2 text-left">{t('dashboard.colStation')}</th>
                    <th className="p-2 text-center">{t('dashboard.colState')}</th>
                    <th className="p-2 text-right">{t('dashboard.colDate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((o, idx) => {
                    const qtySold = o.volume_total - o.volume_remain;
                    const profit = o.is_buy_order
                      ? ((o.jita_buy || 0) - o.price) * qtySold
                      : (o.price - (o.jita_sell || 0)) * qtySold;
                    return (
                    <tr key={`hist-${o.type_id}-${idx}`} className="hover:bg-zinc-900/40 border-b border-zinc-800/50">
                      <td className="p-2">
                        <div className="flex items-center gap-1.5">
                          <span className="text-zinc-300 font-mono truncate max-w-[140px]" title={o.type_name}>{o.type_name}</span>
                          <button onClick={() => { navigator.clipboard.writeText(o.type_name).catch(() => {}); }} className="shrink-0 text-zinc-600 hover:text-zinc-300" title={t('common.copy')}>
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                      <td className="p-2 text-zinc-500">{o.char_name}</td>
                      <td className="p-2 text-center">{o.is_buy_order === true ? <span className="text-blue-400">B</span> : o.is_buy_order === false ? <span className="text-green-400">S</span> : <span className="text-zinc-600">—</span>}</td>
                      <td className="p-2 text-right text-zinc-300 font-mono" title={o.price.toLocaleString()}>{fmtShort(o.price)}</td>
                      <td className="p-2 text-right text-zinc-500 font-mono" title={`S: ${(o.jita_sell || 0).toLocaleString()}  B: ${(o.jita_buy || 0).toLocaleString()}`}>{o.jita_sell ? fmtShort(o.jita_sell) : '—'}/{o.jita_buy ? fmtShort(o.jita_buy) : '—'}</td>
                      <td className={`p-2 text-right font-mono ${profit > 0 ? 'text-green-400' : profit < 0 ? 'text-red-400' : 'text-zinc-600'}`} title={profit.toLocaleString()}>{profit > 0 ? '+' : ''}{fmtShort(profit)}</td>
                      <td className="p-2 text-right text-zinc-500 font-mono">{o.volume_remain}/{o.volume_total}</td>
                      <td className="p-2 text-zinc-500 font-mono text-xs truncate max-w-[120px]" title={o.station_name}>{o.station_name || '—'}</td>
                      <td className="p-2 text-center"><span className={`text-xs font-mono ${o.state === 'completed' ? 'text-green-500' : o.state === 'cancelled' ? 'text-red-400' : 'text-zinc-500'}`}>{o.state}</span></td>
                      <td className="p-2 text-right text-zinc-500 font-mono whitespace-nowrap">{o.display_date ? new Date(o.display_date).toLocaleDateString() : ''}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            ) : (
              !historyLoading && <div className="p-4 text-center text-zinc-600 text-sm italic">{t('dashboard.historyEmpty')}</div>
            );
          })()}
          {historyOrders.length < historyTotal && (
            <div className="p-3 text-center">
              <button onClick={() => loadHistory(historyPage + 1, historyFilter)} disabled={historyLoading}
                className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-400 px-4 py-1.5 rounded font-mono transition-colors disabled:opacity-50">
                {historyLoading ? t('common.loading') : t('dashboard.historyLoadMore')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Categories */}
      {CATEGORY_ORDER.map(cat => {
        const items = grouped[cat];
        if (!items || items.length === 0) return null;
        const attn = attentionCount(cat);
        const isExpanded = expandedCategories.get(cat) ?? (cat === 'Favorites'); // only Favorites open by default

        return (
          <div key={cat} className="mb-4 border border-zinc-800 rounded-sm overflow-hidden">
            <button onClick={() => toggleCategory(cat)} className="w-full flex items-center justify-between px-4 py-2.5 bg-zinc-900/50 hover:bg-zinc-900 transition-colors">
              <div className="flex items-center gap-2">
                <span className="text-sm text-zinc-300 font-mono">{CATEGORY_LABELS[cat] || cat}</span>
                <span className="text-xs text-zinc-500 font-mono">{items.length}</span>
                {attn > 0 && <span className="text-xs text-red-400 font-mono animate-pulse">● {attn}</span>}
              </div>
              <span className="text-zinc-600 text-xs">{isExpanded ? '▾' : '▸'}</span>
            </button>
            {isExpanded && (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-zinc-800 bg-zinc-950 text-zinc-500">
                      <th className="p-2 font-mono">{t('dashboard.colName')}</th>
                      <th className="p-2 font-mono">{t('dashboard.colStation')}</th>
                      <th className="p-2 font-mono text-right">{t('dashboard.colQty')}</th>
                      <th className="p-2 font-mono text-right">{t('dashboard.colPrice')}</th>
                      <th className="p-2 font-mono text-right">{t('dashboard.colJita')}</th>
                      <th className="p-2 font-mono text-right">{t('dashboard.colDiff')}</th>
                      <th className="p-2 font-mono">{t('dashboard.colOwner')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-900">
                    {[...items].sort((a, b) => {
                      const aAttn = !a.is_buy_order && !a.is_first && !a.is_blocked_hub;
                      const bAttn = !b.is_buy_order && !b.is_first && !b.is_blocked_hub;
                      if (aAttn !== bAttn) return aAttn ? -1 : 1;
                      const aDiff = parseFloat(pctDiff(a.price, a.jita_sell) || 0);
                      const bDiff = parseFloat(pctDiff(b.price, b.jita_sell) || 0);
                      return bDiff - aDiff;
                    }).map((o, idx) => {
                      const isFav = favorites.has(o.type_id);
                      const diff = pctDiff(o.price, o.jita_sell);
                      return (
                        <tr key={`${o.order_id}-${idx}`} onClick={() => setFocusedRow(`${o.order_id}-${idx}`)} className={`cursor-pointer ${focusedRow === `${o.order_id}-${idx}` ? 'bg-zinc-800/50' : ''} hover:bg-zinc-900/40 ${o.is_blocked_hub ? 'opacity-30' : ''}`}>
                          <td className="p-2">
                            <div className="flex items-center gap-1.5">
                              {!o.is_buy_order && !o.is_first && !o.is_blocked_hub && (
                                <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" title={t('dashboard.notFirst')} />
                              )}
                              {o.is_buy_order ? <span className="text-blue-400 text-xs font-mono shrink-0">B</span> : <span className="text-green-400 text-xs font-mono shrink-0">S</span>}
                              <button onClick={() => toggleFavorite(o.type_id, o.type_name)} className={`shrink-0 text-base ${isFav ? 'text-yellow-400' : 'text-zinc-700 hover:text-zinc-400'}`}>
                                {isFav ? '★' : '☆'}
                              </button>
                              <span className="truncate max-w-[200px]" title={o.type_name}>{o.type_name}</span>
                              <button onClick={() => copyName(o.type_name)} className="shrink-0 text-zinc-600 hover:text-zinc-300" title={t('common.copy')}>
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          </td>
                          <td className="p-2 text-zinc-500 font-mono">{o.station_name}</td>
                          <td className="p-2 text-zinc-300 font-mono text-right">{o.volume_remain}</td>
                          <td className="p-2 text-zinc-300 font-mono text-right">{formatPrice(o.price)}</td>
                          <td className="p-2 text-zinc-500 font-mono text-right">{formatPrice(o.jita_sell)}</td>
                          <td className={`p-2 font-mono text-right ${diff !== null ? (parseFloat(diff) < 0 ? 'text-green-400' : 'text-red-400') : 'text-zinc-700'}`}>
                            {diff !== null ? `${diff}%` : '—'}
                          </td>
                          <td className="p-2 text-zinc-500 font-mono">{o.owner_name}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {orders.length === 0 && !checking && (
        <div className="text-center py-20 text-zinc-600 text-sm italic">{t('dashboard.noOrdersYet')}</div>
      )}

    </div>
   );
}
}

function PlaceholderPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Добавляем стейты прямо сюда
  const [stations, setStations] = useState([]);
  const [newStationName, setNewStationName] = useState('');
  const [editingStationId, setEditingStationId] = useState(null);
  const [editingStationName, setEditingStationName] = useState('');
  const [newStationId, setNewStationId] = useState('');
  const [loading, setLoading] = useState(true);
  const [detectingStation, setDetectingStation] = useState(false);
  const [detectedHint, setDetectedHint] = useState('');
  const [currentStationDetails, setCurrentStationDetails] = useState(null);
  const [showStationRenameHint, setShowStationRenameHint] = useState(null);
  const [marketInfo, setMarketInfo] = useState({});
  const [addingStationIds, setAddingStationIds] = useState({});

  const [quickStations, setQuickStations] = useState([]);

  const handleQuickAdd = async (st) => {
    if (stations.some(s => Number(s.structure_id) === st.id)) return;
    setAddingStationIds(prev => ({ ...prev, [st.id]: true }));
    try {
      await api.post('/api/stations', {
        name: st.name,
        structure_id: st.id,
        details: { system_name: st.system }
      });
      loadStations();
    } catch (err) {
      console.error(err);
    } finally {
      setAddingStationIds(prev => ({ ...prev, [st.id]: false }));
    }
  };

  // Загрузка данных при открытии страницы
  const loadStations = async () => {
    try {
      const res = await api.get('/api/stations');
      const data = res.data || [];
      setStations(data);
      try {
        const qsRes = await api.get('/api/stations/quick-add');
        setQuickStations(qsRes.data || []);
      } catch (e) {
        console.error('Failed to load quick-add stations:', e);
      }
      const ids = data.map(s => s.structure_id).filter(Boolean).join(',');
      if (ids) {
        const mi = await api.get(`/api/stations/market-info?station_ids=${ids}`);
        setMarketInfo(mi.data || {});
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStations(); }, []);

  const addStation = async () => {
    if (!newStationName.trim() || !newStationId.trim()) return;
    try {
      const payload = {
        name: newStationName,
        structure_id: parseInt(newStationId)
      };
      console.log("currentStationDetails:", currentStationDetails);
      if (currentStationDetails) {
        payload.details = currentStationDetails;
      }
      console.log("Sending station payload:", payload);
      await api.post('/api/stations', payload);
      setNewStationName('');
      setNewStationId('');
      setDetectedHint('');
      setCurrentStationDetails(null);
      loadStations(); 
    } catch (err) {
      console.error("Ошибка добавления станции:", err);
    }
  };

const detectAndAddStation = async () => {
    setDetectingStation(true);
    try {
      const res = await api.post('/api/stations/detect-current');
      const data = res?.data;
      
      if (data?.status === 'in_space') {
        setDetectedHint(data.message || t('dashboard.inSpace'));
        setDetectingStation(false);
        return;
      }
      
      const detectedId = data?.structure_id;
      const suggestedName = data?.suggested_name || '';
      const details = data?.details;
      
      if (!detectedId) {
        setDetectedHint(t('dashboard.cantDetectStation'));
        setDetectingStation(false);
        return;
      }
      
      const payload = {
        name: suggestedName || `Station ${detectedId}`,
        structure_id: detectedId
      };
      if (details) {
        payload.details = details;
      }
      
      await api.post('/api/stations', payload);
      setDetectedHint(t('dashboard.stationAdded'));
      loadStations(); 
    } catch (err) {
      const msg = err?.response?.data?.detail || t('dashboard.error');
      setDetectedHint(msg);
} finally {
      setDetectingStation(false);
    }
  };

const detectCurrentStation = async () => {
    setDetectingStation(true);
    try {
      const res = await api.post('/api/stations/detect-current');
      console.log("detect-current response:", res.data);
      const data = res?.data;
      
      if (data?.status === 'in_space') {
        setDetectedHint(data.message || t('dashboard.inSpace'));
        setNewStationName('');
        setNewStationId('');
        setCurrentStationDetails(null);
        return;
      }
      
      const detectedId = data?.structure_id;
      const suggestedName = data?.suggested_name || '';
      if (detectedId) setNewStationId(String(detectedId));
      if (suggestedName) setNewStationName(suggestedName);
      setCurrentStationDetails(data?.details || null);
      setDetectedHint(data?.message || t('dashboard.stationDetected'));
    } catch (err) {
      const msg = err?.response?.data?.detail || t('dashboard.cantDetectCurrent');
      setDetectedHint('');
      alert(msg);
    } finally {
      setDetectingStation(false);
    }
  };

  const removeStation = async (id) => {
    if (!window.confirm(t('dashboard.deleteStation'))) return;
    try {
      await api.delete(`/api/stations/${id}`);
      loadStations();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="animate-fadeIn max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-medium text-white">{t('common.stations')}</h1>
            <p className="text-text-muted text-sm mt-1">{t('dashboard.stationsDesc', { name: user?.char_name || t('dashboard.unknown') })}</p>
          </div>
          <button onClick={() => navigate('/dashboard')} className="text-sm bg-zinc-800 hover:bg-zinc-700 px-4 py-2 rounded transition-colors text-white">
            {t('dashboard.backToDashboard')}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            <div className="border border-border bg-black p-5">
              <h3 className="text-xs font-mono uppercase tracking-widest text-primary mb-4">{t('dashboard.newLocation')}</h3>
              <p className="text-[11px] text-text-muted mb-4">
                {t('dashboard.addStationManually')}
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] uppercase text-text-muted mb-1">{t('dashboard.stationName')}</label>
                  <input 
                    type="text" 
                    value={newStationName}
                    onChange={(e) => setNewStationName(e.target.value)}
                    placeholder={t('dashboard.stationPlaceholder')}
                    className="w-full bg-zinc-950 border border-border p-2 text-sm text-white outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase text-text-muted mb-1">{t('dashboard.stationId')}</label>
<input
  type="number"
  value={newStationId}
  onChange={(e) => setNewStationId(e.target.value)}
  placeholder={t('dashboard.stationIdPlaceholder')}
  className="w-full bg-zinc-950 border border-border p-2 text-sm text-white outline-none focus:border-primary"
/>
                </div>
                {currentStationDetails?.system_name && (
                  <div>
                    <label className="block text-[10px] uppercase text-text-muted mb-1">{t('dashboard.system')}</label>
                    <div className="w-full bg-zinc-950 border border-border p-2 text-sm text-white">
                      {currentStationDetails.system_name}
                    </div>
                  </div>
                )}
                <button onClick={addStation} className="w-full bg-primary text-black py-2 text-sm font-bold flex items-center justify-center gap-2">
                  <Plus className="w-4 h-4" /> {t('common.add')}
                </button>
              </div>
            </div>

            <div className="border border-border bg-black p-5 mt-6">
              <h3 className="text-xs font-mono uppercase tracking-widest text-primary mb-4">{t('dashboard.detectLocation')}</h3>
              <p className="text-[11px] text-text-muted mb-3">
                {t('dashboard.currentChar')}: <span className="text-white font-mono">{user?.char_name || t('dashboard.unknown')}</span>
              </p>
              <p className="text-[11px] text-text-muted mb-4">
                {t('dashboard.detectHint')}
              </p>
              <button
                onClick={detectCurrentStation}
                disabled={detectingStation}
                className="flex-1 bg-primary text-black py-2 text-sm font-bold hover:bg-primary-hover disabled:opacity-50"
              >
                {detectingStation ? t('dashboard.detecting') : t('dashboard.detectAndFill')}
              </button>
              {detectedHint && newStationId && (
                <button 
                  onClick={() => {
                    addStation();
                  }}
                  className="flex-1 border border-border text-white py-2 text-sm hover:bg-zinc-900"
                >
                  {t('dashboard.addStation')}
                </button>
              )}
              {detectedHint && (
                <p className="text-[11px] text-emerald-300 mt-3">{detectedHint}</p>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
              <div className="border border-border bg-black">
              <div className="p-3 border-b border-border bg-zinc-900/30">
                <p className="text-[11px] text-amber-400/80 font-mono leading-relaxed">
                  {t('dashboard.tradeHubsBlocked')}
                </p>
              </div>
              <div className="p-3 border-b border-border">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">{t('dashboard.quickAddStations')}</p>
                <div className="flex flex-wrap gap-2">
                  {quickStations.map(st => {
                    const exists = stations.some(s => Number(s.structure_id) === st.id);
                    const orders = exists && marketInfo[st.id] ? marketInfo[st.id] * 1000 : null;
                    return (
                      <button
                        key={st.id}
                        onClick={() => handleQuickAdd(st)}
                        disabled={exists || addingStationIds[st.id]}
                        className={`text-[11px] font-mono px-3 py-2 rounded-sm border transition-all ${
                          exists
                            ? 'bg-zinc-900/20 border-zinc-700/30 text-zinc-500 cursor-not-allowed'
                            : 'bg-zinc-900/60 border-zinc-700/50 text-zinc-400 hover:border-cyan-700/50 hover:text-cyan-300'
                        }`}
                      >
                        <div className="flex items-center gap-1.5">
                          {st.owner ? (
                            <img
                              src={`https://images.evetech.net/${st.owner.type}/${st.owner.id}/logo?size=32`}
                              alt=""
                              className="w-3.5 h-3.5 rounded-sm shrink-0"
                            />
                          ) : (
                            <Building2 className="w-3 h-3 shrink-0" strokeWidth={1.5} />
                          )}
                          {st.system} · {st.name}
                        </div>
                        {orders !== null && (
                          <div className="text-[10px] text-zinc-600 mt-0.5">≈{orders.toLocaleString()} {t('dashboard.marketOrders')}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-border bg-zinc-900/50 text-text-muted">
                    <th className="p-3 text-xs uppercase font-mono">{t('dashboard.stationName')}</th>
                    <th className="p-3 text-xs uppercase font-mono hidden md:table-cell">{t('dashboard.system')}</th>
                    <th className="p-3 text-xs uppercase font-mono">{t('dashboard.stationId')}</th>
                    <th className="p-3 w-16"></th>
                  </tr>
                </thead>
                <tbody className="text-white">
                  {stations.length > 0 ? (
                    stations.map(st => {
                      const qs = quickStations.find(q => q.id === Number(st.structure_id));
                      return (
                      <tr key={st.id} className="border-b border-border/50 hover:bg-zinc-900/20">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {qs?.owner ? (
                              <div className="w-8 h-8 flex items-center justify-center shrink-0">
                                <img src={`https://images.evetech.net/${qs.owner.type}/${qs.owner.id}/logo?size=32`} alt="" className="w-6 h-6 rounded-sm" />
                              </div>
                            ) : (
                              <div className="w-8 h-8 bg-zinc-900 border border-zinc-800 rounded flex items-center justify-center shrink-0">
                                <Building2 className="w-4 h-4 text-cyan-700" strokeWidth={1.5} />
                              </div>
                            )}
                            <div>
                              {editingStationId === st.id ? (
                                <input
                                  type="text"
                                  value={editingStationName}
                                  onChange={(e) => setEditingStationName(e.target.value)}
                                  onBlur={async () => {
                                    if (editingStationName.trim()) {
                                      await api.put(`/api/stations/${st.id}`, { name: editingStationName.trim() });
                                      loadStations();
                                    }
                                    setEditingStationId(null);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      if (editingStationName.trim()) {
                                        api.put(`/api/stations/${st.id}`, { name: editingStationName.trim() }).then(loadStations);
                                      }
                                      setEditingStationId(null);
                                    }
                                    if (e.key === 'Escape') {
                                      setEditingStationId(null);
                                    }
                                  }}
                                  className="text-sm font-medium bg-transparent border-b border-cyan-500 outline-none"
                                  autoFocus
                                />
                              ) : (
                                <div className="relative">
                                  <div 
                                    className="text-sm font-medium cursor-pointer hover:text-cyan-400"
                                    onClick={() => {
                                      setEditingStationId(st.id);
                                      setEditingStationName(st.name);
                                    }}
                                    onMouseEnter={() => setShowStationRenameHint(st.id)}
                                    onMouseLeave={() => setShowStationRenameHint(null)}
                                  >
                                    {st.name}
                                  </div>
                                  {showStationRenameHint === st.id && (
                                    <div className="absolute left-0 top-full mt-1 px-2 py-1 bg-zinc-800 text-zinc-300 text-[10px] rounded whitespace-nowrap z-50">
                                      {t('dashboard.renameHint')}
                                    </div>
                                  )}
                                </div>
                              )}
                              {st.type_name && <div className="text-[10px] text-zinc-600 font-mono">{st.type_name}</div>}
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-sm text-zinc-400 hidden md:table-cell">
                          {st.system_name || <span className="text-zinc-700">—</span>}
                        </td>
                        <td className="p-3 text-sm font-mono text-zinc-600">{st.structure_id}</td>
                        <td className="p-3 text-right">
                          <button onClick={() => removeStation(st.id)} className="text-red-500/40 hover:text-red-500 p-1" title={t('common.delete')}>
                            <X className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    )})
                  ) : (
                    <tr>
                      <td colSpan="5" className="p-10 text-center text-text-muted text-sm italic">{t('dashboard.emptyList')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== SHARED PACKAGE PAGE =====
function SharedPackagePage() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [prices, setPrices] = useState({});

  useEffect(() => {
    const fetchShared = async () => {
      try {
        const res = await api.get(`/api/shared/${token}`);
        const data = res.data;
        setPkg(data);
        if (data.items?.length) {
          const ids = data.items.map(i => i.type_id).join(',');
          const priceRes = await api.get(`/api/prices?type_ids=${ids}`);
          setPrices(priceRes.data);
        }
      } catch (err) {
        setError(err?.response?.data?.detail || t('shared.notFound'));
      } finally {
        setLoading(false);
      }
    };
    fetchShared();
  }, [token]);

  useEffect(() => {
    const checkAuth = async () => {
      const tok = localStorage.getItem('session_token');
      if (tok) {
        try {
          const res = await api.get('/api/auth/me');
          setUser(res.data);
          if (res.data?.language) {
            i18n.changeLanguage(res.data.language);
          }
        } catch {}
      }
    };
    checkAuth();
  }, []);

  const handleCopy = async () => {
    if (!user) {
      window.location.href = '/api/auth/login';
      return;
    }
    setCopying(true);
    try {
      await api.post(`/api/packages/${pkg.id}/copy`);
      setCopied(true);
    } catch (err) {
      alert(err?.response?.data?.detail || t('shared.copyError'));
    } finally {
      setCopying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-zinc-500 font-mono animate-pulse">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-zinc-200 mb-2">{t('shared.notFound')}</h1>
          <p className="text-zinc-500 mb-6">{error}</p>
          <a href="/" className="text-cyan-400 hover:underline">{t('shared.backToHome')}</a>
        </div>
      </div>
    );
  }

  const multiplier = pkg.multiplier || 1;
  const readyCount = pkg.items?.filter(i => (i.available_qty || 0) >= i.required_qty * multiplier).length || 0;
  const totalItems = pkg.items?.length || 0;
  const readyPct = totalItems > 0 ? readyCount / totalItems : 1;
  const maxBatches = pkg.items?.length ? Math.min(...pkg.items.map(i => Math.floor((i.available_qty || 0) / i.required_qty))) : 0;
  const isOwnPackage = pkg.is_yours && user;
  const totalCost = pkg.items?.reduce((sum, i) => {
    const price = i.sell_price || prices[i.type_id];
    return price ? sum + price * i.required_qty : sum;
  }, 0) || 0;

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-200">
      <header className="border-b border-zinc-800 p-4 flex items-center justify-between">
        <a href="/" className="text-zinc-400 hover:text-white transition-colors text-sm">← {t('shared.backToHome')}</a>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <span className="text-xs text-zinc-600 font-mono flex items-center gap-1.5">
            <Globe className="w-3 h-3" /> {t('shared.sharedPackage')}
          </span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6">
          <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-white mb-1">{pkg.name}</h1>
              {pkg.station_name && (
                <p className="text-sm text-zinc-400 flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5" />
                  {pkg.station_name}
                  {pkg.corporation_name && <span className="text-zinc-600">· {pkg.corporation_name}</span>}
                  {pkg.alliance_name && <span className="text-zinc-600">· {pkg.alliance_name}</span>}
                  {totalCost > 0 && (
                    <span className="text-xs text-zinc-500 font-mono bg-zinc-800/60 border border-zinc-700/40 px-2 py-0.5 rounded-full" title={t('dashboard.priceWithoutMultiplier')}>
                      ≈ {totalCost.toLocaleString(undefined, { maximumFractionDigits: 0 })} ISK
                    </span>
                  )}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {multiplier > 1 && maxBatches > multiplier && (
                <span
                  className="text-xs text-green-400 font-mono bg-green-500/10 border border-green-500/20 px-2 py-1 rounded-full"
                  title={t('shared.sufficientForBatches', { n: maxBatches })}
                >
                  ×{maxBatches}
                </span>
              )}
              {multiplier > 1 && (
                <span
                  className="text-xs text-cyan-400 font-mono bg-cyan-500/10 border border-cyan-500/20 px-2 py-1 rounded-full"
                  title={t('shared.multiplierTitle')}
                >
                  ×{multiplier}
                </span>
              )}
              <span className={`text-xs font-mono ${readyPct >= 1 ? 'text-green-400' : readyPct < 0.3 ? 'text-red-400' : 'text-yellow-400'}`}>
                {readyCount}/{totalItems} {t('shared.ready')}
              </span>
            </div>
          </div>

        <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
          {isOwnPackage ? (
            <a
              href="/dashboard"
              className="px-4 py-2 rounded-sm text-sm font-medium transition-all flex items-center gap-2 bg-cyan-500 text-black hover:bg-cyan-400"
            >
              ← {t('shared.openInDashboard')}
            </a>
          ) : copied ? (
            <a
              href="/dashboard"
              className="px-4 py-2 rounded-sm text-sm font-medium transition-all flex items-center gap-2 bg-green-600 text-white hover:bg-green-500"
            >
              ← {t('shared.openInDashboard')}
            </a>
          ) : (
            <button
              onClick={handleCopy}
              disabled={copying}
              className={`px-4 py-2 rounded-sm text-sm font-medium transition-all flex items-center gap-2
                ${user 
                  ? 'bg-cyan-500 text-black hover:bg-cyan-400' 
                  : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}
            >
              <Copy className="w-4 h-4" />
              {copying ? t('shared.copying') : user ? t('shared.copyToDashboard') : t('shared.loginToSave')}
            </button>
          )}
          <p className="text-xs text-zinc-500 text-right">
            {pkg.last_checked_at
              ? t('shared.dataActual', { date: new Date(pkg.last_checked_at).toLocaleString([], { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' }) })
              : t('shared.dataActualNone')}
          </p>
        </div>

        <div className="border border-zinc-800 bg-black rounded-sm overflow-x-auto">
          <table className="w-full text-left min-w-[500px]">
            <thead>
              <tr className="border-b border-zinc-800 bg-[#0c0c0d] text-zinc-400">
                <th className="p-4 text-xs uppercase tracking-wider font-mono">{t('shared.item')}</th>
                <th className="p-4 text-xs uppercase tracking-wider text-center font-mono">{t('shared.need')}</th>
                <th className="p-4 text-xs uppercase tracking-wider text-center font-mono">{t('shared.have')}</th>
                <th className="p-4 text-xs uppercase tracking-wider text-center font-mono">{t('shared.status')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900/40">
              {pkg.items
                ?.slice()
                .sort((a, b) => {
                  const getPriority = (i) => {
                    const qty = i.available_qty;
                    if (qty === null || qty === undefined) return 5;
                    if (qty === 0) return 0;
                    const r = qty / (i.required_qty * multiplier);
                    if (r >= 5) return 4;  // Много
                    if (r >= 1) return 3;  // Достаточно
                    if (r >= 0.7) return 2; // Недостаточно
                    if (r >= 0.3) return 1;  // Мало
                    return 0;               // Критично
                  };
                  return getPriority(a) - getPriority(b);
                })
                  .map(item => {
                const needed = item.required_qty * multiplier;
                const ratio = item.available_qty / needed;
                return (
                  <tr key={item.id} className="hover:bg-zinc-950 transition-colors">
                         <td className="p-4 text-sm font-medium text-zinc-200">
                       <button
                         onClick={() => { navigator.clipboard.writeText(item.type_name); }}
                         className="hover:text-cyan-400 transition-colors cursor-pointer"
                         title={t('shared.copyName')}
                       >
                         {item.type_name}
                       </button>
                     </td>
                    <td className="p-4 text-center font-mono text-zinc-400">{needed}</td>
                    <td className="p-4 text-center font-mono text-zinc-400">{item.available_qty ?? '—'}</td>
                    <td className="p-4 text-center">
                       {item.available_qty !== null ? (
                        ratio >= 5 ? (
                          <span className="inline-flex items-center gap-1.5 text-cyan-400 bg-cyan-500/5 px-2.5 py-1 rounded-full text-xs font-medium border border-cyan-900/40">
                            <Check className="w-3.5 h-3.5" /> {t('shared.plenty')}
                          </span>
                        ) : ratio >= 1 ? (
                          <span className="inline-flex items-center gap-1.5 text-green-400 bg-green-500/5 px-2.5 py-1 rounded-full text-xs font-medium border border-green-900/40">
                            <Check className="w-3.5 h-3.5" /> {t('shared.enough')}
                          </span>
                        ) : ratio >= 0.7 ? (
                          <span className="inline-flex items-center gap-1.5 text-yellow-400 bg-yellow-500/5 px-2.5 py-1 rounded-full text-xs font-medium border border-yellow-900/40">
                            <AlertCircle className="w-3.5 h-3.5" /> {t('shared.insufficient')}
                          </span>
                        ) : ratio >= 0.3 ? (
                          <span className="inline-flex items-center gap-1.5 text-red-400 bg-red-500/5 px-2.5 py-1 rounded-full text-xs font-medium border border-red-900/40">
                            <AlertCircle className="w-3.5 h-3.5" /> {t('shared.low')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-red-600 bg-red-600/5 px-2.5 py-1 rounded-full text-xs font-bold border border-red-900/60">
                            <X className="w-3.5 h-3.5" /> {item.available_qty === 0 ? "0" : t('shared.critical')}
                          </span>
                        )
                      ) : (
                        <span className="text-zinc-700 font-mono">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

// ===== ADMIN PAGE =====
function AdminPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [userPackages, setUserPackages] = useState({});
  const [userStations, setUserStations] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(null);
  const [adminStations, setAdminStations] = useState(null);
  const [astLoading, setAstLoading] = useState(false);
  const [editAst, setEditAst] = useState(null); // {id, structure_id, system_name, name, owner_type, owner_id, is_active, sort_order}
  const [newAst, setNewAst] = useState({ structure_id: '', system_name: '', name: '', owner_type: '', owner_id: '', sort_order: 0 });

  const loadAdminStations = async () => {
    setAstLoading(true);
    try {
      const res = await api.get('/api/admin/quick-stations');
      setAdminStations(res.data);
    } catch (e) {
      console.error('Failed to load admin stations:', e);
    }
    setAstLoading(false);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get('/api/admin/users');
        setUsers(res.data);
      } catch {
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
    loadAdminStations();
  }, []);

  const saveAdminStation = async (record) => {
    if (record.id) {
      await api.put(`/api/admin/quick-stations/${record.id}`, record);
    } else {
      await api.post('/api/admin/quick-stations', record);
    }
    await loadAdminStations();
    setEditAst(null);
    setNewAst({ structure_id: '', system_name: '', name: '', owner_type: '', owner_id: '', sort_order: 0 });
  };

  const deleteAdminStation = async (id) => {
    if (!confirm('Delete this station?')) return;
    await api.delete(`/api/admin/quick-stations/${id}`);
    await loadAdminStations();
  };

  const toggleExpand = async (userId) => {
    if (expandedId === userId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(userId);
    if (!userPackages[userId] || !userStations[userId]) {
      setLoadingDetails(userId);
      try {
        const [pkgRes, staRes] = await Promise.all([
          api.get(`/api/admin/users/${userId}/packages`),
          api.get(`/api/admin/users/${userId}/stations`),
        ]);
        setUserPackages(prev => ({ ...prev, [userId]: pkgRes.data }));
        setUserStations(prev => ({ ...prev, [userId]: staRes.data }));
      } catch {}
      setLoadingDetails(null);
    }
  };

  const copyShareLink = async (shareToken) => {
    if (!shareToken) return;
    const link = `${window.location.origin}/shared/${shareToken}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      alert('Copy failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-zinc-500 font-mono animate-pulse">{t('common.loading')}</div>
      </div>
    );
  }

  if (user?.char_name !== 'anton68 Meza') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-zinc-200 mb-2">Access Denied</h1>
          <p className="text-zinc-500 mb-6">You do not have permission to access this page.</p>
          <a href="/dashboard" className="text-cyan-400 hover:underline">← Back to Dashboard</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-medium text-white">Admin Panel</h1>
          <button onClick={() => window.history.back()} className="text-sm bg-zinc-800 hover:bg-zinc-700 px-4 py-2 rounded transition-colors text-white">
            ← Back
          </button>
        </div>

        <div className="border border-border bg-black">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-border bg-zinc-900/50 text-text-muted">
                <th className="p-3 text-xs uppercase font-mono">Character</th>
                <th className="p-3 text-xs uppercase font-mono hidden md:table-cell">Telegram</th>
                <th className="p-3 text-xs uppercase font-mono">Lang</th>
                <th className="p-3 text-xs uppercase font-mono text-center">Lists</th>
                <th className="p-3 text-xs uppercase font-mono text-center">Orders</th>
                <th className="p-3 text-xs uppercase font-mono hidden lg:table-cell">Last Login</th>
              </tr>
            </thead>
            <tbody className="text-white">
              {users && users.length > 0 ? (
                users.map(u => (
                  <React.Fragment key={u.id}>
                    <tr
                      className="border-b border-border/50 hover:bg-zinc-900/20 cursor-pointer"
                      onClick={() => toggleExpand(u.id)}
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-zinc-900 border border-zinc-800 rounded flex items-center justify-center shrink-0">
                            <Users className="w-4 h-4 text-cyan-700" strokeWidth={1.5} />
                          </div>
                          <div>
                            {u.characters && u.characters.length > 0 ? (
                              u.characters.map((ch, ci) => (
                                <div key={ch.char_id} className="font-mono text-sm">
                                  <span className={ch.is_main ? 'text-white' : 'text-zinc-400'}>
                                    {ch.char_name}
                                    {ch.is_main && <span className="text-cyan-600 text-[10px] ml-1">●</span>}
                                    {u.corp_ticker ? <span className="text-zinc-500"> [{u.corp_ticker}]</span> : ''}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <div className="font-mono text-sm text-white">
                                {u.char_name}
                                {u.corp_ticker ? <span className="text-zinc-500"> [{u.corp_ticker}]</span> : ''}
                              </div>
                            )}
                            {u.alliance_name && (
                              <div className="text-xs text-zinc-600 font-mono">{u.alliance_name}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-3 font-mono text-sm hidden md:table-cell">
                        {u.chat_id ? (
                          <span className="text-emerald-400">✓ Connected</span>
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )}
                      </td>
                      <td className="p-3 font-mono text-sm text-zinc-400">
                        {u.language || '—'}
                      </td>
                      <td className="p-3 font-mono text-sm text-zinc-400 text-center">
                        {u.package_count}
                      </td>
                      <td className="p-3 font-mono text-sm text-zinc-400 text-center">
                        {u.order_count}
                      </td>
                      <td className="p-3 font-mono text-sm text-zinc-500 hidden lg:table-cell">
                        {u.last_login ? new Date(u.last_login).toLocaleString() : '—'}
                      </td>
                    </tr>
                    {expandedId === u.id && (
                      <tr key={`${u.id}-detail`} className="bg-zinc-900/20">
                        <td colSpan={6} className="p-0">
                          <div className="border-b border-border/30 p-4 pl-14 space-y-4">
                            {/* Characters — always available from main data */}
                            {u.characters && u.characters.length > 0 && (
                              <div>
                                <h4 className="text-xs font-mono uppercase tracking-widest text-primary mb-2">Characters</h4>
                                <div className="space-y-1">
                                  {u.characters.map(ch => (
                                    <div key={ch.char_id} className="flex items-center gap-3 text-sm">
                                      <span className="font-mono text-zinc-300">
                                        {ch.char_name}
                                        {ch.is_main ? <span className="text-cyan-600 text-[10px] ml-1">● main</span> : ''}
                                      </span>
                                      <span className="font-mono text-zinc-500">
                                        {ch.order_count} orders
                                      </span>
                                      {ch.last_check ? (
                                        <span className="font-mono text-zinc-600 text-xs">
                                          {new Date(ch.last_check).toLocaleString()}
                                        </span>
                                      ) : (
                                        <span className="font-mono text-zinc-700 text-xs">—</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {loadingDetails === u.id ? (
                              <div className="text-zinc-500 text-xs font-mono animate-pulse">Loading...</div>
                            ) : (
                              <>
                                {/* Packages */}
                                <div>
                                  <h4 className="text-xs font-mono uppercase tracking-widest text-primary mb-2">Packages</h4>
                                  {userPackages[u.id]?.length > 0 ? (
                                    <div className="space-y-1.5">
                                      {userPackages[u.id].map(pkg => (
                                        <div key={pkg.id} className="flex items-center gap-3 text-sm">
                                          <span className="text-zinc-300 font-mono">
                                            {pkg.name} ({pkg.item_count})
                                            <span className="text-zinc-600">
                                              {' '}· {pkg.check_interval_minutes || '?'}m
                                              {pkg.notify_enabled ? (
                                                <span className="text-emerald-500"> ON</span>
                                              ) : (
                                                <span className="text-zinc-600"> OFF</span>
                                              )}
                                            </span>
                                          </span>
                                          {pkg.share_token && (
                                            <button
                                              onClick={() => copyShareLink(pkg.share_token)}
                                              className="text-cyan-500 hover:text-cyan-400 transition-colors"
                                              title="Copy share link"
                                            >
                                              <Copy className="w-3.5 h-3.5" />
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-zinc-600 text-xs font-mono">No packages</span>
                                  )}
                                </div>
                                {/* Stations */}
                                <div>
                                  <h4 className="text-xs font-mono uppercase tracking-widest text-primary mb-2">Stations</h4>
                                  {userStations[u.id]?.length > 0 ? (
                                    <div className="space-y-1">
                                      {userStations[u.id].map(st => (
                                        <div key={st.id} className="text-sm text-zinc-400 font-mono">
                                          {st.name}
                                          {st.system_name ? ` (${st.system_name})` : ''}
                                          {' '}#{st.structure_id}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-zinc-600 text-xs font-mono">No stations</span>
                                  )}
                                </div>
                                {/* Features */}
                                <div>
                                  <h4 className="text-xs font-mono uppercase tracking-widest text-primary mb-2">Features</h4>
                                  <div className="flex flex-wrap gap-4 text-sm">
                                    {[
                                      { key: 'show_lists', label: 'Списки', default: true },
                                      { key: 'show_orders', label: 'Ордера', default: true },
                                      { key: 'show_trader', label: 'Трейдер', default: false },
                                    ].map(f => {
                                      const val = u[f.key] ?? f.default;
                                      return (
                                        <label key={f.key} className="flex items-center gap-1.5 cursor-pointer text-zinc-400 hover:text-zinc-200 transition-colors">
                                          <input type="checkbox"
                                            checked={val}
                                            onChange={async () => {
                                              try {
                                                await api.post(`/api/admin/users/${u.id}/features`, { [f.key]: !val });
                                                setUsers(prev => prev.map(uu => uu.id === u.id ? { ...uu, [f.key]: !val } : uu));
                                              } catch (e) { console.error(e); }
                                            }}
                                            className="w-3.5 h-3.5 accent-cyan-500 cursor-pointer" />
                                          {f.label}
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-zinc-600 text-sm font-mono">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Quick-Add Stations Management */}
        <div className="border border-border bg-black mt-8">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="text-lg font-medium text-white">Quick-Add Stations</h2>
            <button
              onClick={() => setEditAst({ structure_id: '', system_name: '', name: '', owner_type: '', owner_id: '', is_active: true, sort_order: 0 })}
              className="text-xs bg-zinc-800 hover:bg-zinc-700 px-3 py-1.5 rounded transition-colors text-zinc-300 font-mono"
            >
              + Add
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-border bg-zinc-900/50 text-text-muted">
                  <th className="p-3 text-xs uppercase font-mono">Name</th>
                  <th className="p-3 text-xs uppercase font-mono">System</th>
                  <th className="p-3 text-xs uppercase font-mono">Structure ID</th>
                  <th className="p-3 text-xs uppercase font-mono">Owner</th>
                  <th className="p-3 text-xs uppercase font-mono text-center">Active</th>
                  <th className="p-3 text-xs uppercase font-mono text-center w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="text-white">
                {astLoading ? (
                  <tr><td colSpan={6} className="p-6 text-center text-zinc-600 text-sm font-mono animate-pulse">Loading...</td></tr>
                ) : adminStations && adminStations.length > 0 ? (
                  adminStations.map(as => (
                    <tr key={as.id} className="border-b border-border/50 hover:bg-zinc-900/20">
                      <td className="p-3 font-mono text-sm">{as.name}</td>
                      <td className="p-3 font-mono text-sm text-zinc-400">{as.system_name}</td>
                      <td className="p-3 font-mono text-sm text-zinc-500">{as.structure_id}</td>
                      <td className="p-3 font-mono text-sm text-zinc-500">
                        {as.owner_type ? `${as.owner_type}/${as.owner_id}` : '—'}
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={async () => {
                            await saveAdminStation({ id: as.id, is_active: !as.is_active });
                          }}
                          className={`text-xs px-2 py-0.5 rounded font-mono ${
                            as.is_active ? 'text-emerald-400 bg-emerald-900/30 border border-emerald-800/40' : 'text-zinc-600 bg-zinc-900/50 border border-zinc-800'
                          }`}
                        >
                          {as.is_active ? 'ON' : 'OFF'}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setEditAst({ ...as })}
                            className="text-zinc-500 hover:text-cyan-400 transition-colors p-1"
                            title="Edit"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          <button
                            onClick={() => deleteAdminStation(as.id)}
                            className="text-zinc-500 hover:text-red-400 transition-colors p-1"
                            title="Delete"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={6} className="p-6 text-center text-zinc-600 text-sm font-mono">No stations</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Edit/Create Station Modal */}
        {editAst !== null && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setEditAst(null)}>
            <div className="bg-zinc-900 border border-zinc-700 rounded-sm p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-medium text-white mb-4">{editAst.id ? 'Edit Station' : 'Add Station'}</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Name</label>
                  <input value={editAst.name || ''} onChange={e => setEditAst({ ...editAst, name: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm text-white font-mono outline-none focus:border-cyan-700 mt-1" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">System</label>
                  <input value={editAst.system_name || ''} onChange={e => setEditAst({ ...editAst, system_name: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm text-white font-mono outline-none focus:border-cyan-700 mt-1" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Structure ID</label>
                  <input type="number" value={editAst.structure_id || ''} onChange={e => setEditAst({ ...editAst, structure_id: e.target.value ? parseInt(e.target.value) : '' })} className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm text-white font-mono outline-none focus:border-cyan-700 mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Owner Type</label>
                    <select value={editAst.owner_type || ''} onChange={e => setEditAst({ ...editAst, owner_type: e.target.value || null })} className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm text-white font-mono outline-none focus:border-cyan-700 mt-1">
                      <option value="">None</option>
                      <option value="alliances">Alliance</option>
                      <option value="corporations">Corporation</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Owner ID</label>
                    <input type="number" value={editAst.owner_id || ''} onChange={e => setEditAst({ ...editAst, owner_id: e.target.value ? parseInt(e.target.value) : null })} className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm text-white font-mono outline-none focus:border-cyan-700 mt-1" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Sort Order</label>
                  <input type="number" value={editAst.sort_order || 0} onChange={e => setEditAst({ ...editAst, sort_order: parseInt(e.target.value) || 0 })} className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm text-white font-mono outline-none focus:border-cyan-700 mt-1" />
                </div>
              </div>
              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={async () => {
                    await saveAdminStation(editAst);
                  }}
                  className="bg-cyan-700 hover:bg-cyan-600 text-white text-sm font-mono px-4 py-2 rounded transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditAst(null)}
                  className="text-zinc-400 hover:text-white text-sm font-mono px-4 py-2 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/shared/:token" element={<SharedPackagePage />} />
          <Route path="/stations" element={<ProtectedRoute><PlaceholderPage title="Управление станциями" /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/trader" element={<ProtectedRoute><TraderPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;